import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../hooks/useTheme';
import { useNavigate } from 'react-router-dom';
import api from '../../api/api';
import { seatPrice, seatTotal } from '../../utils/orgPricing';
//old import { CreditCard, Users, Edit, Plus, Save, X, CheckCircle, XCircle, Search, Eye, Loader2, Ticket, Power, GraduationCap, UserCog, Trash2, Building2 } from 'lucide-react';
import { CreditCard, Users, Edit, Plus, Save, X, CheckCircle, XCircle, Search, Eye, Ticket, Power, GraduationCap, UserCog, Trash2, Building2 } from 'lucide-react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

// Per-tier monthly usage limits are edited here on the Membership Tiers page
// (centralized) rather than on each feature's own admin page. These are the
// features that have a FeatureLimits row; keys must match FeatureName in the DB.
// `audience` matches a MembershipTypes.MembershipTypeName so the editor can grey
// out limits that don't apply to the tier's audience. Note the key is
// FeatureLimits.FeatureName, which is NOT always the matching Features.FeatureKey
// (the CSI retake limit is 'csi' while the premium feature is
// 'csi_recommendations'), so the audience is declared here rather than looked up.
// `featureKey` is the matching Features.FeatureKey — it drives the order of the
// "Unlocks features" picker so both lists read in the same order, and it isn't
// always the limit key (the CSI retake limit is 'csi', the premium feature is
// 'csi_recommendations').
const FEATURE_LIMIT_DEFS = [
    { key: 'resume_analysis', label: 'Resume Analysis', audience: 'Student', featureKey: 'resume_analysis' },
    { key: 'org_checklist', label: 'Resume Checklist', audience: 'Student', featureKey: 'org_checklist' },
    { key: 'mock_interview', label: 'Mock Interview', audience: 'Student', featureKey: 'mock_interview' },
    { key: 'elevator_pitch', label: 'Elevator Pitch', audience: 'Student', featureKey: 'elevator_pitch' },
    //old { key: 'csi', label: 'CSI', audience: 'Student', featureKey: 'csi_recommendations' },
    // The CSI limit is how often the CSI can be retaken a month (Alicia, Sep 4: 3); it stays 3 on paid plans too (csi.py)
    { key: 'csi', label: 'CSI retakes', audience: 'Student', featureKey: 'csi_recommendations', paidDefault: 3 },
    { key: 'coach_reporting', label: 'Coach Reporting', audience: 'Coach', featureKey: 'coach_reporting', metered: false },
    { key: 'my_program', label: 'My Program', audience: 'Coach', featureKey: 'my_program', metered: false },
];

// The word people see for an audience (JJ, Nov 2: client, not student), as orgUi's LICENSE_TYPE_LABEL; the stored
// name stays 'Student' (MembershipTypes, Features.Category and the audiences above)
const AUDIENCE_LABEL = { Student: 'Client' };

// Canonical display order, shared by the feature picker and the limit fields.
const FEATURE_ORDER = FEATURE_LIMIT_DEFS.map((f) => f.featureKey);
const featureRank = (key) => {
    const i = FEATURE_ORDER.indexOf(key);
    return i === -1 ? FEATURE_ORDER.length : i; // anything unlisted sorts last
};

// Blank value for every defined limit, derived from the defs so adding a feature
// can't leave it out of form state (which would render an uncontrolled input).
const emptyLimits = () => Object.fromEntries(FEATURE_LIMIT_DEFS.map((f) => [f.key, '']));

// Resume Analysis, Mock Interview and Elevator Pitch are only counted by their monthly
// limit (since Sep 10 / Oct 2 their switch in Unlocks features did nothing), so the
// picker leaves them out: a feature whose key is its own metered limit
const LIMIT_ONLY_KEYS = FEATURE_LIMIT_DEFS.filter((f) => f.metered !== false && f.key === f.featureKey).map((f) => f.key);

// Community Partner packages: 25 free licenses, and 50 / 100 = those 25 plus 25 / 75 paid ones
// (the backend's FREE_PARTNER_LICENSES and PARTNER_PACKAGES)
const PARTNER_FREE = 25;
const PARTNER_PAID = [25, 75];
// A typed dollar amount in whole cents (NaN when it isn't one)
const toCents = (text) => (String(text).trim() === '' ? NaN : Math.round(Number(String(text).replace(/[$,\s]/g, '')) * 100));
const centsText = (cents) => `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
// The Community Partner plan's packages for its tier card, priced like the sign-up page (org_service._package):
// 25 free, then 50 and 100 at the price per license less the biggest discount step each reaches ("50 = $499/yr")
const partnerPackagesText = (tier) => [`${PARTNER_FREE} free`, ...PARTNER_PAID.map((paid) => {
    const step = (tier.GroupDiscounts || []).filter((d) => paid >= Number(d.MinQuantity))
        .reduce((best, d) => (!best || Number(d.MinQuantity) > Number(best.MinQuantity) ? d : best), null);
    const cents = Math.round(seatTotal(seatPrice(tier.Price, step ? step.PercentOff : 0), paid) * 100);
    return `${PARTNER_FREE + paid} = ${cents % 100 ? centsText(cents) : `$${(cents / 100).toLocaleString('en-US')}`}/${tier.BillingFrequency === 'yearly' ? 'yr' : 'mo'}`;
})].join(' · ');

export default function MembershipManagement() {
    const { user, can } = useAuth();
    const { isDarkMode } = useTheme();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('student');
    const [tiers, setTiers] = useState([]);
    // Saving a tier makes its Stripe prices (the backend has a Stripe key; JJ, Oct 28): Edit Tier's Price IDs are optional
    const [autoPrices, setAutoPrices] = useState(false);
    const [memberships, setMemberships] = useState([]);
    const [membershipTypes, setMembershipTypes] = useState([]);
    const [features, setFeatures] = useState([]);
    const [codes, setCodes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    // Save errors for the open tier / code modal: the page banner sits behind its overlay
    const [modalError, setModalError] = useState('');
    const [editingTier, setEditingTier] = useState(null);
    const [showTierModal, setShowTierModal] = useState(false);
    const [editingCode, setEditingCode] = useState(null);
    const [showCodeModal, setShowCodeModal] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [pagination, setPagination] = useState({ page: 1, per_page: 20, total: 0 });
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [showExpired, setShowExpired] = useState(false);
    const [paymentDetails, setPaymentDetails] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);

    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    useEffect(() => {
        if (!user || !user.isAdmin) {
            navigate('/userdashboard');
        }
    }, [user, navigate]);

    const fetchTiers = useCallback(async () => {
        try {
            setLoading(true);
            const response = await api.get('/membership/admin/tiers');
            setTiers(response.data.tiers || []);
            setAutoPrices(Boolean(response.data.autoStripePrices));
        } catch (err) {
            console.error('Error fetching tiers:', err);
            setError('Failed to load membership tiers');
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchMemberships = useCallback(async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams({
                page: pagination.page,
                per_page: pagination.per_page,
            });
            if (debouncedSearch) params.set('search', debouncedSearch);
            if (showExpired) params.set('show_expired', 'true');
            const response = await api.get(`/membership/admin/memberships?${params.toString()}`);
            setMemberships(response.data.memberships || []);
            setPagination(prev => ({ ...prev, total: response.data.total || 0, total_pages: response.data.total_pages || 1 }));
        } catch (err) {
            console.error('Error fetching memberships:', err);
            setError('Failed to load active memberships');
        } finally {
            setLoading(false);
        }
    }, [pagination.page, pagination.per_page, debouncedSearch, showExpired]);

    // Audience types back the tier modal's checkboxes — load them once up front.
    const fetchTypes = useCallback(async () => {
        try {
            const response = await api.get('/membership/admin/membership-types');
            setMembershipTypes(response.data.types || []);
        } catch (err) {
            console.error('Error fetching membership types:', err);
        }
    }, []);

    const fetchFeatures = useCallback(async () => {
        try {
            const response = await api.get('/membership/admin/features');
            setFeatures(response.data.features || []);
        } catch (err) {
            console.error('Error fetching features:', err);
        }
    }, []);

    const fetchCodes = useCallback(async () => {
        try {
            setLoading(true);
            const response = await api.get('/membership/admin/referral-codes');
            setCodes(response.data.codes || []);
        } catch (err) {
            console.error('Error fetching referral codes:', err);
            //old setError('Failed to load referral codes');
            setError('Failed to load promo codes');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchTypes(); fetchFeatures(); }, [fetchTypes, fetchFeatures]);

    useEffect(() => {
        if (['student', 'coach', 'tiers'].includes(activeTab)) fetchTiers();
        else if (activeTab === 'members') fetchMemberships();
        else if (activeTab === 'codes') fetchCodes();
    }, [activeTab, pagination.page, fetchTiers, fetchMemberships, fetchCodes]);

    React.useEffect(() => {
        if (!showTierModal && !showCodeModal) setModalError('');
    }, [showTierModal, showCodeModal]);

    const flashSuccess = (msg) => {
        setSuccess(msg);
        setTimeout(() => setSuccess(''), 3000);
    };

    // Upsert per-feature monthly usage limits for a tier. Reuses the existing
    // /admin/feature-limits endpoints: PUT when the tier already has a row for
    // that feature, POST to create otherwise. A blank value means "leave
    // unchanged" (there is no delete endpoint for a limit).
    const saveFeatureLimits = async (tierId, featureLimits) => {
        for (const fl of featureLimits) {
            if (fl.value === '' || fl.value === null || fl.value === undefined) continue;
            //const monthlyLimit = parseInt(fl.value, 10);
            // The limit column is an INT, so anything bigger is saved as its max
            const monthlyLimit = Math.min(parseInt(fl.value, 10), 2147483647);
            if (isNaN(monthlyLimit) || monthlyLimit < 0) continue;
            if (fl.id) {
                await api.put(`/admin/feature-limits/${fl.id}`, { monthlyLimit });
            } else {
                //await api.post('/admin/feature-limits', {
                //    tierID: tierId, featureName: fl.feature, monthlyLimit,
                //});
                try {
                    await api.post('/admin/feature-limits', {
                        tierID: tierId, featureName: fl.feature, monthlyLimit,
                    });
                } catch (err) {
                    // Already saved by an earlier Save that failed partway: update that row
                    if (err.response?.status !== 409) throw err;
                    const res = await api.get('/admin/feature-limits', { params: { feature: fl.feature } });
                    const row = (res.data.limits || []).find((r) => r.TierID === tierId);
                    if (!row) throw err;
                    await api.put(`/admin/feature-limits/${row.FeatureLimitID}`, { monthlyLimit });
                }
            }
        }
    };

    const handleSaveTier = async (tierData) => {
        try {
            setSaving(true);
            setError('');
            setModalError('');
            setSuccess('');
            // feature_limits rides along on tierData but isn't a tier column; split it out.
            const { feature_limits, ...tierPayload } = tierData;
            let tierId;
            if (editingTier) {
                await api.put(`/membership/admin/tiers/${editingTier.TierID}`, tierPayload);
                tierId = editingTier.TierID;
                flashSuccess('Tier updated successfully!');
            } else {
                const res = await api.post('/membership/admin/tiers', tierPayload);
                tierId = res.data?.tier_id;
                // The tier exists now: if the limits below fail, Save again
                // updates this tier instead of creating a second one
                if (tierId) setEditingTier({ TierID: tierId, TierName: tierPayload.tier_name });
                flashSuccess('Tier created successfully!');
            }
            // Centralized usage limits: set each feature's monthly quota in the same save.
            if (tierId && Array.isArray(feature_limits)) {
                await saveFeatureLimits(tierId, feature_limits);
            }
            setShowTierModal(false);
            setEditingTier(null);
            fetchTiers();
        } catch (err) {
            //setError(err.response?.data?.error || 'Failed to save tier');
            setModalError(err.response?.data?.error || 'Failed to save tier');
            // Part of the save may have gone through (the tier, some limits)
            fetchTiers();
        } finally {
            setSaving(false);
        }
    };

    const handleSaveCode = async (codeData) => {
        try {
            setSaving(true);
            setError('');
            setModalError('');
            if (editingCode) {
                await api.put(`/membership/admin/referral-codes/${editingCode.MembershipReferralCodeID}`, codeData);
                //old flashSuccess('Referral code updated!');
                flashSuccess('Promo code updated!');
            } else {
                await api.post('/membership/admin/referral-codes', codeData);
                //old flashSuccess('Referral code created!');
                flashSuccess('Promo code created!');
            }
            setShowCodeModal(false);
            setEditingCode(null);
            fetchCodes();
        } catch (err) {
            //setError(err.response?.data?.error || 'Failed to save referral code');
            //old setModalError(err.response?.data?.error || 'Failed to save referral code');
            setModalError(err.response?.data?.error || 'Failed to save promo code');
        } finally {
            setSaving(false);
        }
    };

    const handleToggleCode = async (code) => {
        try {
            await api.patch(`/membership/admin/referral-codes/${code.MembershipReferralCodeID}/status`, { is_active: !code.isActive });
            fetchCodes();
        } catch (err) {
            //old setError(err.response?.data?.error || 'Failed to update referral code');
            setError(err.response?.data?.error || 'Failed to update promo code');
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    };
    // The day only, for the dates a plan given at no charge runs
    const formatDay = (dateString) => (dateString
        ? new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A');

    const isExpired = (endDate) => new Date(endDate) < new Date();

    const openNewTier = () => { setEditingTier(null); setShowTierModal(true); };
    const openEditTier = (tier) => { setEditingTier(tier); setShowTierModal(true); };

    const hasAudience = (tier, name) => (tier.Audiences || []).includes(name);
    const studentTiers = tiers.filter(t => t.IsActive && hasAudience(t, 'Student'));
    const coachTiers = tiers.filter(t => t.IsActive && hasAudience(t, 'Coach'));

    const tabDef = [
        //old { key: 'student', label: 'Active Student Membership', Icon: GraduationCap, permission: 'Student Memberships' },
        { key: 'student', label: 'Active Client Membership', Icon: GraduationCap, permission: 'Student Memberships' },
        { key: 'coach', label: 'Active Coach Membership', Icon: UserCog, permission: 'Coach Memberships' },
        { key: 'tiers', label: 'Membership Tiers', Icon: CreditCard, permission: 'Membership Tiers' },
        //old { key: 'codes', label: 'Referral Codes', Icon: Ticket, permission: 'Referral Codes' },
        { key: 'codes', label: 'Promo Codes', Icon: Ticket, permission: 'Referral Codes' },
        { key: 'members', label: 'Active Members', Icon: Users, permission: 'Active Members' },
    ].filter(tab => can('Memberships', tab.permission));

    // Open the first tab this role can view
    useEffect(() => {
        if (tabDef.length && !tabDef.some(tab => tab.key === activeTab)) setActiveTab(tabDef[0].key);
    }, [tabDef, activeTab]);

    if (loading && tiers.length === 0 && memberships.length === 0 && codes.length === 0) {
        return (
            <div className={`flex items-center justify-center min-h-screen ${isDarkMode ? 'bg-surface' : 'bg-surface'}`}>
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
            </div>
        );
    }

    const audienceBadge = (name) => {
        const map = {
            Student: 'bg-sky-100 text-sky-800',
            Coach: 'bg-indigo-100 text-indigo-800',
            Enterprise: 'bg-amber-100 text-amber-800',
        };
        return map[name] || 'bg-slate-100 text-slate-700';
    };

    const TierCard = ({ tier }) => (
        <div className={`border rounded-xl p-4 hover:shadow-md transition-shadow ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
            <div className="flex items-start justify-between">
                <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                        <h3 className={`text-lg font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>{tier.TierName}</h3>
                        {tier.IsActive ? (
                            <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded">Active</span>
                        ) : (
                            <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-semibold rounded">Inactive</span>
                        )}
                        {(tier.Audiences || []).map((a) => (
                            //old <span key={a} className={`px-2 py-1 text-xs font-semibold rounded ${audienceBadge(a)}`}>{a}</span>
                            <span key={a} className={`px-2 py-1 text-xs font-semibold rounded ${audienceBadge(a)}`}>{AUDIENCE_LABEL[a] || a}</span>
                        ))}
                        {(!tier.Audiences || tier.Audiences.length === 0) && (
                            <span className="px-2 py-1 bg-slate-100 text-slate-500 text-xs font-semibold rounded">No audience</span>
                        )}
                        {/* Only one tier holds this, so surface it in the list —
                            otherwise you'd have to open each tier to find it. */}
                        {(tier.IsReferralReward === true || tier.IsReferralReward === 1) && (
                            <span className="px-2 py-1 bg-amber-100 text-amber-800 text-xs font-semibold rounded">🎁 Referral reward</span>
                        )}
                        {Boolean(tier.IsCommunityPartnerTier) && (
                            <span className="px-2 py-1 bg-emerald-100 text-emerald-800 text-xs font-semibold rounded">🤝 Community Partner plan</span>
                        )}
                        {Boolean(tier.SoldToOrganizations) && (
                            <span className="px-2 py-1 bg-violet-100 text-violet-800 text-xs font-semibold rounded">🏢 Group pricing</span>
                        )}
                    </div>
                    <p className={`mb-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>{tier.TierDescription || 'No description'}</p>
                    {/* old <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm"> */}
                    {/* one column on a phone: two columns left a price running into the box next to it */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2 sm:gap-4 text-sm">
                        <div>
                            <span className="text-slate-500">Price:</span>
                            <span className="ml-2 font-semibold">${tier.Price != null ? Number(tier.Price).toFixed(2) : '0.00'}</span>
                        </div>
                        {/* Free plans only: on a paid plan's card it read like a free trial (Edit Tier still shows it,
                            with what it's for: how long a free promo code gives the plan) */}
                        {!(Number(tier.Price) > 0) && (
                        <div>
                            {/* old <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Duration:</span> */}
                            <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Days given free:</span>
                            {/* old <span className={`ml-2 font-semibold ${isDarkMode ? 'text-slate-200' : ''}`}>{tier.DurationDays} days</span> */}
                            <span className={`ml-2 font-semibold ${isDarkMode ? 'text-slate-200' : ''}`}>{tier.DurationDays}</span>
                        </div>
                        )}
                        {Number(tier.Price) > 0 && (
                            <div>
                                <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Billing:</span>
                                <span className={`ml-2 font-semibold ${isDarkMode ? 'text-slate-200' : ''}`}>{tier.BillingFrequency}</span>
                            </div>
                        )}
                        {tier.PaymentLink && (
                            <div>
                                <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Price ID:</span>
                                <span className={`ml-2 font-mono text-xs ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>{tier.PaymentLink}</span>
                            </div>
                        )}
                    </div>
                    {/* Group pricing: what an organization pays per seat at each volume step */}
                    {/* old {Boolean(tier.SoldToOrganizations) && ( */}
                    {/* (the Community Partner plan shows its packages below instead) */}
                    {Boolean(tier.SoldToOrganizations) && !tier.IsCommunityPartnerTier && (
                        <div className={`mt-3 text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                            <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Group pricing:</span>
                            <span className="ml-2">
                                {tier.GroupDiscounts?.length > 0
                                    ? tier.GroupDiscounts.map((d) => (
                                        //`${d.MinQuantity}+ licenses → $${(Number(tier.Price) * (100 - d.PercentOff) / 100).toFixed(2)} each (${Math.round(d.PercentOff * 100) / 100}% off)`
                                        `${d.MinQuantity}+ licenses → $${seatPrice(tier.Price, d.PercentOff).toFixed(2)} each (${Math.round(d.PercentOff * 100) / 100}% off)`
                                    )).join('  ·  ')
                                    : `$${Number(tier.Price).toFixed(2)} per license / ${tier.BillingFrequency === 'yearly' ? 'year' : 'month'}, no volume discount`}
                            </span>
                        </div>
                    )}
                    {/* The Community Partner plan: Alicia's packages (Oct 22), as its sign-up page prices them */}
                    {Boolean(tier.IsCommunityPartnerTier) && (
                        <div className={`mt-3 text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                            <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Packages:</span>
                            <span className="ml-2">
                                {Number(tier.Price) > 0 ? partnerPackagesText(tier) : `${PARTNER_FREE} free (50 and 100 not priced yet)`}
                            </span>
                            {Number(tier.Price) > 0 && !tier.SoldToOrganizations && (
                                <span className={`ml-2 text-xs ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>Group pricing is off: online payment opens soon</span>
                            )}
                        </div>
                    )}
                </div>
                <button
                    onClick={() => openEditTier(tier)}
                    className={`ml-4 p-2 rounded-lg transition-colors ${isDarkMode ? 'text-slate-400 hover:text-blue-400 hover:bg-slate-700' : 'text-slate-600 hover:text-blue-600 hover:bg-blue-50'}`}
                >
                    <Edit size={18} />
                </button>
            </div>
        </div>
    );

    const tierListPanel = (title, subtitle, list, showNew) => (
        <div className={`rounded-2xl border shadow-sm p-6 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h2 className={`text-xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>{title}</h2>
                    {subtitle && <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>{subtitle}</p>}
                </div>
                {showNew && (
                    <button onClick={openNewTier} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors">
                        <Plus size={18} /> New Tier
                    </button>
                )}
            </div>
            <div className="space-y-4">
                {list.map((tier) => <TierCard key={tier.TierID} tier={tier} />)}
                {list.length === 0 && !loading && (
                    <p className={`text-center py-8 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>No tiers to show.</p>
                )}
            </div>
        </div>
    );

    return (
        <div className={`min-h-screen ${isDarkMode ? 'bg-surface' : 'bg-surface'}`}>
            <div className="max-w-7xl mx-auto px-4 py-8">
                {/* Header */}
                <header className="mb-8">
                    <h1 className={`text-3xl font-extrabold tracking-tight mb-2 ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Membership Management</h1>
                    {/* old <p className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>Manage membership tiers by audience, referral codes, and view active members</p> */}
                    <p className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>Manage membership tiers by audience, promo codes, and view active members</p>
                </header>

                {/* Tabs */}
                <div className={`mb-6 border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                    <div className="flex gap-4 flex-wrap">
                        {tabDef.map(({ key, label, Icon }) => (
                            <button
                                key={key}
                                onClick={() => setActiveTab(key)}
                                className={`px-4 py-2 font-semibold border-b-2 transition-colors ${
                                    activeTab === key
                                        ? 'border-blue-600 text-blue-600'
                                        : isDarkMode
                                            ? 'border-transparent text-slate-400 hover:text-slate-200'
                                            : 'border-transparent text-slate-600 hover:text-slate-900'
                                }`}
                            >
                                <Icon size={18} className="inline mr-2" />
                                {label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Messages */}
                {error && (
                    <div className={`mb-4 p-4 border rounded-lg ${isDarkMode ? 'bg-red-900/30 border-red-800 text-red-300' : 'bg-red-50 border-red-200 text-red-800'}`}>
                        {error}
                    </div>
                )}
                {success && (
                    <div className={`mb-4 p-4 border rounded-lg ${isDarkMode ? 'bg-green-900/30 border-green-800 text-green-300' : 'bg-green-50 border-green-200 text-green-800'}`}>
                        {success}
                    </div>
                )}

                {/* Active Student Membership */}
                {activeTab === 'student' && tierListPanel(
                    //old 'Active Student Membership',
                    'Active Client Membership',
                    //old 'Active tiers available to students.',
                    'Active tiers available to clients.',
                    studentTiers,
                    false,
                )}

                {/* Active Coach Membership */}
                {activeTab === 'coach' && tierListPanel(
                    'Active Coach Membership',
                    'Active tiers available to coaches.',
                    coachTiers,
                    false,
                )}

                {/* Membership Tiers — all tiers, active and inactive, full edit */}
                {activeTab === 'tiers' && tierListPanel(
                    'Membership Tiers',
                    'All memberships — active and inactive. Create and edit tiers here.',
                    tiers,
                    true,
                )}

                {/* Referral Codes Tab */}
                {activeTab === 'codes' && (
                    <div className={`rounded-2xl border shadow-sm p-6 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                        <div className="flex justify-between items-center mb-6">
                            <div>
                                {/* old <h2 className={`text-xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Referral Codes</h2> */}
                                <h2 className={`text-xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Promo Codes</h2>
                                {/* old <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Codes that grant a membership tier (e.g. a free coach account).</p> */}
                                <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Codes that give a membership tier for free or at a discount (e.g. a free coach account).</p>
                            </div>
                            <button
                                onClick={() => { setEditingCode(null); setShowCodeModal(true); }}
                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                            >
                                <Plus size={18} />
                                New Code
                            </button>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className={isDarkMode ? 'bg-slate-700' : 'bg-slate-50'}>
                                    <tr>
                                        {/* old {['Code', 'Grants Tier', 'Discount', 'Redeemed', 'Expires', 'Status', ''].map((h, i) => ( */}
                                        {['Code', 'Grants Tier', 'Discount', 'Redeemed', 'Offer ends', 'Status', ''].map((h, i) => (
                                            <th key={i} className={`px-4 py-3 text-left text-xs font-semibold uppercase ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-slate-200'}`}>
                                    {codes.map((c) => (
                                        <tr key={c.MembershipReferralCodeID} className={isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-50'}>
                                            <td className={`px-4 py-3 font-mono font-semibold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>{c.Code}</td>
                                            <td className="px-4 py-3 text-sm">
                                                <div className="flex flex-wrap gap-1">
                                                    {(c.Tiers || []).map(t => (
                                                        <span key={t.TierID} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded">{t.TierName}</span>
                                                    ))}
                                                    {(!c.Tiers || c.Tiers.length === 0) && <span className="text-xs text-slate-400">—</span>}
                                                </div>
                                            </td>
                                            <td className={`px-4 py-3 text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>{describeDiscount(c)}</td>
                                            <td className={`px-4 py-3 text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                                {c.TimesRedeemed || 0}{c.MaxRedemptions != null ? ` / ${c.MaxRedemptions}` : ''}
                                            </td>
                                            <td className={`px-4 py-3 text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                                {c.ExpiresAt ? new Date(c.ExpiresAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—'}
                                            </td>
                                            <td className="px-4 py-3">
                                                {/* {c.ExpiresAt && new Date(c.ExpiresAt) < new Date() ? ( */}
                                                {/* Same rule as redeeming: the code works through the end of its date */}
                                                {(c.IsExpired !== undefined ? Boolean(c.IsExpired) : (c.ExpiresAt && new Date(c.ExpiresAt) < new Date())) ? (
                                                    <span className="flex items-center gap-1 text-red-500"><XCircle size={16} /><span className="text-xs font-semibold">Expired</span></span>
                                                ) : c.isActive ? (
                                                    <span className="flex items-center gap-1 text-green-600"><CheckCircle size={16} /><span className="text-xs font-semibold">Active</span></span>
                                                ) : (
                                                    <span className="flex items-center gap-1 text-slate-500"><XCircle size={16} /><span className="text-xs font-semibold">Inactive</span></span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        onClick={() => handleToggleCode(c)}
                                                        title={c.isActive ? 'Deactivate' : 'Activate'}
                                                        className={`p-2 rounded-lg transition-colors ${c.isActive ? 'text-amber-600 hover:bg-amber-50' : 'text-green-600 hover:bg-green-50'} ${isDarkMode ? 'hover:bg-slate-700' : ''}`}
                                                    >
                                                        <Power size={16} />
                                                    </button>
                                                    <button
                                                        onClick={() => { setEditingCode(c); setShowCodeModal(true); }}
                                                        className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'text-slate-400 hover:text-blue-400 hover:bg-slate-700' : 'text-slate-600 hover:text-blue-600 hover:bg-blue-50'}`}
                                                    >
                                                        <Edit size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    {codes.length === 0 && !loading && (
                                        //old <tr><td colSpan={7} className={`text-center py-8 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>No referral codes yet.</td></tr>
                                        <tr><td colSpan={7} className={`text-center py-8 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>No promo codes yet.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Members Tab */}
                {activeTab === 'members' && (
                    <div className={`rounded-2xl border shadow-sm p-6 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                        <div className="flex justify-between items-center mb-6">
                            <h2 className={`text-xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Active Members</h2>
                        </div>

                        {/* Filters */}
                        <div className="flex flex-wrap items-center gap-4 mb-6">
                            <div className="relative flex-1 min-w-[200px] max-w-md">
                                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                <input
                                    type="text"
                                    placeholder="Search by name or email..."
                                    value={searchTerm}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        setPagination(prev => ({ ...prev, page: 1 }));
                                    }}
                                    className={`w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isDarkMode ? 'bg-slate-900 border-slate-600 text-slate-200 placeholder-slate-500' : 'border-slate-300'}`}
                                />
                            </div>
                            <label className="flex items-center gap-2 cursor-pointer select-none">
                                <input
                                    type="checkbox"
                                    checked={showExpired}
                                    onChange={(e) => {
                                        setShowExpired(e.target.checked);
                                        setPagination(prev => ({ ...prev, page: 1 }));
                                    }}
                                    className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Show expired</span>
                            </label>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className={isDarkMode ? 'bg-slate-700' : 'bg-slate-50'}>
                                    <tr>
                                        <th className={`px-4 py-3 text-left text-xs font-semibold uppercase ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>User</th>
                                        <th className={`px-4 py-3 text-left text-xs font-semibold uppercase ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>Tier</th>
                                        <th className={`px-4 py-3 text-left text-xs font-semibold uppercase ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>Start Date</th>
                                        <th className={`px-4 py-3 text-left text-xs font-semibold uppercase ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>End Date</th>
                                        <th className={`px-4 py-3 text-left text-xs font-semibold uppercase ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>Status</th>
                                        <th className="px-4 py-3"></th>
                                    </tr>
                                </thead>
                                <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-slate-200'}`}>
                                    {memberships.map((membership) => (
                                        <tr key={membership.MembershipID} className={isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-50'}>
                                            <td className="px-4 py-3">
                                                <div>
                                                    <div className={`font-semibold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                                                        {membership.FirstName} {membership.LastName}
                                                    </div>
                                                    <div className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{membership.EmailAddress}</div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded">{membership.TierName}</span>
                                            </td>
                                            <td className={`px-4 py-3 text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>{formatDate(membership.StartDate)}</td>
                                            <td className="px-4 py-3">
                                                <div className={`text-sm ${isExpired(membership.EndDate) ? 'text-red-600 font-semibold' : isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                                                    {formatDate(membership.EndDate)}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">
                                                {!membership.isActive ? (
                                                    <span className="flex items-center gap-1 text-slate-500">
                                                        <XCircle size={16} />
                                                        <span className="text-xs font-semibold">Inactive</span>
                                                    </span>
                                                ) : isExpired(membership.EndDate) ? (
                                                    <span className="flex items-center gap-1 text-red-600">
                                                        <XCircle size={16} />
                                                        <span className="text-xs font-semibold">Expired</span>
                                                    </span>
                                                ) : (
                                                    <span className="flex items-center gap-1 text-green-600"><CheckCircle size={16} /><span className="text-xs font-semibold">Active</span></span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3">
                                                <button
                                                    onClick={async () => {
                                                        setLoadingDetails(true);
                                                        try {
                                                            const res = await api.get(`/membership/admin/memberships/${membership.MembershipID}/payment-details`);
                                                            setPaymentDetails(res.data);
                                                        } catch (err) {
                                                            setError('Failed to load payment details');
                                                        } finally {
                                                            setLoadingDetails(false);
                                                        }
                                                    }}
                                                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                                                >
                                                    <Eye size={14} />
                                                    Details
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {pagination.total_pages > 1 && (
                            <div className="mt-6 flex items-center justify-between">
                                <div className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                                    Showing {((pagination.page - 1) * pagination.per_page) + 1} to{' '}
                                    {Math.min(pagination.page * pagination.per_page, pagination.total)} of {pagination.total} members
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                                        disabled={pagination.page === 1}
                                        className={`px-4 py-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed ${isDarkMode ? 'border-slate-600 hover:bg-slate-700' : 'border-slate-300 hover:bg-slate-50'}`}
                                    >
                                        Previous
                                    </button>
                                    <button
                                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                                        disabled={pagination.page >= pagination.total_pages}
                                        className={`px-4 py-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed ${isDarkMode ? 'border-slate-600 hover:bg-slate-700' : 'border-slate-300 hover:bg-slate-50'}`}
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Payment Details Modal */}
                        {(paymentDetails || loadingDetails) && (
                            <div className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm" style={{ zIndex: 200 }} onClick={() => !loadingDetails && setPaymentDetails(null)}>
                                <div className={`rounded-2xl shadow-xl max-w-lg w-full mx-4 overflow-hidden ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`} onClick={(e) => e.stopPropagation()}>
                                    <div className={`flex justify-between items-center px-6 py-4 border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                                        <h3 className={`text-lg font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Payment Details</h3>
                                        <button onClick={() => setPaymentDetails(null)} className={`p-1 rounded-lg ${isDarkMode ? 'text-slate-400 hover:text-slate-200 hover:bg-slate-700' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'}`}>
                                            <X size={20} />
                                        </button>
                                    </div>
                                    {loadingDetails ? (
                                        //old <div className="flex items-center justify-center py-12">
                                        <div className="px-6 py-4 space-y-3" aria-busy="true">
                                            {/* old <Loader2 size={24} className="animate-spin text-blue-500" /> */}
                                            {[0, 1, 2].map((i) => <Skeleton key={i} height={40} borderRadius={8}
                                                                            baseColor={isDarkMode ? '#334155' : '#e2e8f0'} highlightColor={isDarkMode ? '#475569' : '#f1f5f9'} />)}
                                        </div>
                                    ) : paymentDetails && (
                                        <div className="px-6 py-4 space-y-4">
                                            <div className="grid grid-cols-2 gap-3 text-sm">
                                                <div>
                                                    <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Member</span>
                                                    <p className={`font-semibold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>{paymentDetails.FirstName} {paymentDetails.LastName}</p>
                                                </div>
                                                <div>
                                                    <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Email</span>
                                                    <p className={`font-semibold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>{paymentDetails.EmailAddress}</p>
                                                </div>
                                                <div>
                                                    <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Tier</span>
                                                    <p className={`font-semibold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>{paymentDetails.TierName}</p>
                                                </div>
                                                {/* A plan nobody paid for (the free plan, or one given by an admin, a free promo
                                                    code or a referral) has no price, payment date or Stripe record to show */}
                                                {paymentDetails.NoPayment && (
                                                    <div className="col-span-2">
                                                        <p className={`font-semibold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>No payment: free plan or given at no charge</p>
                                                        <p className={isDarkMode ? 'text-slate-300' : 'text-slate-600'}>
                                                            {Number(paymentDetails.Price) > 0
                                                                ? `${formatDay(paymentDetails.StartDate)} – ${formatDay(paymentDetails.EndDate)}`
                                                                : `Since ${formatDay(paymentDetails.StartDate)}`}
                                                        </p>
                                                    </div>
                                                )}
                                                {!paymentDetails.NoPayment && (<>
                                                <div>
                                                    <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Price</span>
                                                    <p className={`font-semibold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                                                        ${paymentDetails.Price != null ? Number(paymentDetails.Price).toFixed(2) : 'N/A'}
                                                        {paymentDetails.BillingFrequency && <span className={`font-normal ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}> / {paymentDetails.BillingFrequency}</span>}
                                                    </p>
                                                </div>
                                                <div>
                                                    <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Payment Date</span>
                                                    <p className={`font-semibold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>{formatDate(paymentDetails.PaymentDate)}</p>
                                                </div>
                                                {/* none for a paid plan that lost its link to its Stripe subscription (shown from its checkout below) */}
                                                {paymentDetails.PaymentID != null && (
                                                <div>
                                                    <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Payment ID</span>
                                                    <p className={`font-semibold font-mono text-xs ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>{paymentDetails.PaymentID}</p>
                                                </div>
                                                )}
                                                </>)}
                                            </div>

                                            {/* Stripe Details */}
                                            {!paymentDetails.NoPayment && (
                                            <div className={`border-t pt-4 ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                                                <h4 className={`text-sm font-bold mb-3 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Stripe Details</h4>
                                                <div className="grid grid-cols-2 gap-3 text-sm">
                                                    {paymentDetails.stripe_status && (
                                                        <div>
                                                            <span className="text-slate-500">Subscription Status</span>
                                                            <p className={`font-semibold ${paymentDetails.stripe_status === 'active' ? 'text-green-600' : paymentDetails.stripe_status === 'canceled' ? 'text-red-600' : 'text-slate-900'}`}>
                                                                {paymentDetails.stripe_status}
                                                            </p>
                                                        </div>
                                                    )}
                                                    {paymentDetails.stripe_payment_status && (
                                                        <div>
                                                            <span className="text-slate-500">Payment Status</span>
                                                            <p className={`font-semibold ${paymentDetails.stripe_payment_status === 'paid' ? 'text-green-600' : 'text-slate-900'}`}>
                                                                {paymentDetails.stripe_payment_status}
                                                            </p>
                                                        </div>
                                                    )}
                                                    {paymentDetails.stripe_amount_total != null && (
                                                        <div>
                                                            <span className="text-slate-500">Amount Charged</span>
                                                            <p className="font-semibold text-slate-900">
                                                                ${(paymentDetails.stripe_amount_total / 100).toFixed(2)} {paymentDetails.stripe_currency?.toUpperCase()}
                                                            </p>
                                                        </div>
                                                    )}
                                                    {paymentDetails.stripe_cancel_at_period_end && (
                                                        <div className="col-span-2">
                                                            <span className="text-amber-600 text-xs font-semibold">Cancels at end of period</span>
                                                        </div>
                                                    )}
                                                    {paymentDetails.StripeSubscriptionID && (
                                                        <div className="col-span-2">
                                                            <span className="text-slate-500">Subscription ID</span>
                                                            <p className="font-mono text-xs text-slate-700 break-all">{paymentDetails.StripeSubscriptionID}</p>
                                                        </div>
                                                    )}
                                                    {paymentDetails.VerificationID && (
                                                        <div className="col-span-2">
                                                            <span className="text-slate-500">Session ID</span>
                                                            <p className="font-mono text-xs text-slate-700 break-all">{paymentDetails.VerificationID}</p>
                                                        </div>
                                                    )}
                                                    {/* A plan changed on its subscription (an upgrade is charged on it, no new checkout) or
                                                        renewed was paid after the first checkout: its last payment, so the new plan's price
                                                        isn't read with the first checkout's amount */}
                                                    {paymentDetails.LastPayment && (
                                                        <div className="col-span-2">
                                                            <span className="text-slate-500">Last Payment</span>
                                                            <p className={`font-semibold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                                                                {paymentDetails.LastPayment.Amount != null
                                                                    ? `$${(paymentDetails.LastPayment.Amount / 100).toFixed(2)} ${(paymentDetails.LastPayment.Currency || '').toUpperCase()}`
                                                                    : 'Amount not available'} · {formatDay(paymentDetails.LastPayment.PaidOn)}
                                                            </p>
                                                            <p className={`font-mono text-xs break-all ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>{paymentDetails.LastPayment.InvoiceID}</p>
                                                            <p className={`text-xs mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                                                Payment Date, Payment Status, Amount Charged and Session ID are the first checkout's.
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Tier create/edit modal */}
            {showTierModal && (
                <ModalShell title={editingTier ? 'Edit Tier' : 'New Tier'} onClose={() => { setShowTierModal(false); setEditingTier(null); }} isDarkMode={isDarkMode}>
                    <TierEditForm
                        tier={editingTier}
                        types={membershipTypes}
                        allTiers={tiers}
                        features={features}
                        onSave={handleSaveTier}
                        onCancel={() => { setShowTierModal(false); setEditingTier(null); }}
                        saving={saving}
                        error={modalError}
                        autoPrices={autoPrices}
                    />
                </ModalShell>
            )}

            {/* Referral code create/edit modal */}
            {showCodeModal && (
                //old <ModalShell title={editingCode ? 'Edit Referral Code' : 'New Referral Code'} onClose={() => { setShowCodeModal(false); setEditingCode(null); }} isDarkMode={isDarkMode}>
                <ModalShell title={editingCode ? 'Edit Promo Code' : 'New Promo Code'} onClose={() => { setShowCodeModal(false); setEditingCode(null); }} isDarkMode={isDarkMode}>
                    <CodeEditForm
                        code={editingCode}
                        tiers={tiers}
                        onSave={handleSaveCode}
                        onCancel={() => { setShowCodeModal(false); setEditingCode(null); }}
                        saving={saving}
                        error={modalError}
                    />
                </ModalShell>
            )}
        </div>
    );
}

function describeDiscount(c) {
    if (!c) return '';
    if (c.DiscountType === 'free') return 'Free';
    if (c.DiscountType === 'percent') return `${Number(c.DiscountValue || 0)}% off`;
    if (c.DiscountType === 'amount') return `$${Number(c.DiscountValue || 0).toFixed(2)} off`;
    return c.DiscountType;
}

// Shared centered modal shell (matches the Payment Details modal styling).
function ModalShell({ title, onClose, isDarkMode, children }) {
    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto" style={{ zIndex: 200 }} onClick={onClose}>
            <div className={`rounded-2xl shadow-xl max-w-2xl w-full my-8 max-h-[90vh] flex flex-col overflow-hidden ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`} onClick={(e) => e.stopPropagation()}>
                <div className={`flex-none flex justify-between items-center px-6 py-4 border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                    <h3 className={`text-lg font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>{title}</h3>
                    <button onClick={onClose} className={`p-1 rounded-lg ${isDarkMode ? 'text-slate-400 hover:text-slate-200 hover:bg-slate-700' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'}`}>
                        <X size={20} />
                    </button>
                </div>
                <div className="px-6 py-5 overflow-y-auto">
                    {children}
                </div>
            </div>
        </div>
    );
}

//function TierEditForm({ tier, types, features, allTiers, onSave, onCancel, saving }) {
//old function TierEditForm({ tier, types, features, allTiers, onSave, onCancel, saving, error }) {
// autoPrices: the backend makes the Stripe prices when the tier is saved, so a Price ID is only typed to use one made in Stripe
function TierEditForm({ tier, types, features, allTiers, onSave, onCancel, saving, error, autoPrices }) {
    const { isDarkMode } = useTheme();
    const [formData, setFormData] = React.useState({
        tier_name: tier?.TierName || '',
        tier_description: tier?.TierDescription || '',
        payment_link: tier?.PaymentLink || '',
        // Number boxes keep what the admin types (so a box can be cleared and
        // retyped); they're checked and converted on save.
        price: tier?.Price != null ? String(Number(tier.Price)) : '0',
        billing_frequency: tier?.BillingFrequency || 'monthly',
        duration_days: String(tier?.DurationDays || 30),
        is_active: tier?.IsActive !== undefined ? (tier.IsActive ? 1 : 0) : 1,
        show_in_onboarding: tier?.ShowInOnboarding !== undefined ? Boolean(tier.ShowInOnboarding) : false,
        open_for_enrollment: tier?.OpenForEnrollment !== undefined ? Boolean(tier.OpenForEnrollment) : true,
        is_most_popular: tier?.IsMostPopular !== undefined ? Boolean(tier.IsMostPopular) : false,
        is_referral_reward: tier?.IsReferralReward !== undefined ? Boolean(tier.IsReferralReward) : false,
        is_community_partner_tier: Boolean(tier?.IsCommunityPartnerTier),
        trial_days: tier?.TrialDays || '',
    });
    // What ticking the Community Partner plan changed (Open for new enrollment, billing), put back if it's unticked
    const [beforePartner, setBeforePartner] = React.useState(null);
    // The Price ID boxes opened to use Price IDs made in Stripe (autoPrices; base, steps, cp): otherwise they're made on save
    const [ownIds, setOwnIds] = React.useState({});
    const [audienceIds, setAudienceIds] = React.useState(tier?.AudienceIds || []);
    const [featureIds, setFeatureIds] = React.useState(tier?.FeatureIds || []);
    // Organization sales: seats cost this tier's own price, less a volume discount.
    // Each discount row keeps the typed text of both boxes (percent / price each)
    // and the exact percent that gets saved.
    const [group, setGroup] = React.useState({
        enabled: Boolean(tier?.SoldToOrganizations),
        discounts: (tier?.GroupDiscounts || []).map((d) => ({ min_quantity: d.MinQuantity, pct: d.PercentOff, stripe_price_id: d.StripePriceID || '' })),
    });
    // Narrow boxes for the discount rows: one width class each (w-full would stretch them)
    const boxCls = (width) => `${width} p-2 border rounded-lg ${isDarkMode ? 'bg-slate-900 border-slate-600 text-slate-200' : 'border-slate-300'}`;
    const setDiscount = (i, changes) => setGroup((g) => ({
        ...g, discounts: g.discounts.map((d, j) => (j === i ? { ...d, ...changes } : d)),
    }));
    // Community Partner packages (Alicia, Oct 22: $0 / $499 / $999): 50 = 25 free + 25 paid, so the
    // 50-license price sets this tier's price per license; 100 = 25 free + 75 paid, so the 100-license
    // price sets a discount from 75 licenses. Price and the discounts stay the one thing that's saved
    // (every organization's seats are billed per license in Stripe): the package boxes show what they
    // make, and only a box being typed in shows the admin's own text.
    const [packageEdit, setPackageEdit] = React.useState({});
    // The tier's price changed: show each discount's price again from its percent
    // (the percent is what's saved), not a price typed against the old price.
    React.useEffect(() => {
        setGroup((g) => ({ ...g, discounts: g.discounts.map(({ priceText, ...d }) => d) }));
    }, [formData.price]);
    // Per-feature monthly usage limits for this tier (value = quota, id = existing row).
    const [limits, setLimits] = React.useState(emptyLimits);
    const [limitIds, setLimitIds] = React.useState({});
    // The limits saved before, so a box cleared here can say it keeps its number (a blank box isn't saved)
    const [savedLimits, setSavedLimits] = React.useState({});

    // Prefill the tier's current limits. The GET filters by one feature and
    // returns every tier's row for it, so fetch each feature and pick ours.
    React.useEffect(() => {
        if (!tier?.TierID) return;
        let cancelled = false;
        (async () => {
            try {
                const results = await Promise.all(
                    FEATURE_LIMIT_DEFS.map((f) =>
                        api.get('/admin/feature-limits', { params: { feature: f.key } })
                    )
                );
                if (cancelled) return;
                const vals = {}; const ids = {};
                FEATURE_LIMIT_DEFS.forEach((f, i) => {
                    const row = (results[i].data.limits || []).find((r) => r.TierID === tier.TierID);
                    if (row) { vals[f.key] = row.MonthlyLimit; ids[f.key] = row.FeatureLimitID; }
                });
                setLimits((prev) => ({ ...prev, ...vals }));
                setLimitIds(ids);
                setSavedLimits(vals);
            } catch (e) {
                // Non-fatal: limits just start blank if they can't be loaded.
            }
        })();
        return () => { cancelled = true; };
    }, [tier]);

    // Which other tier currently holds the referral reward, if any — only one
    // tier can, so the form says where it is now.
    const rewardHolder = (allTiers || []).find(
        (t) => (t.IsReferralReward === true || t.IsReferralReward === 1) && t.TierID !== tier?.TierID
    );
    // Same for the Community Partner plan
    const partnerHolder = (allTiers || []).find((t) => Boolean(t.IsCommunityPartnerTier) && t.TierID !== tier?.TierID);

    const priceNum = formData.price === '' ? null : Number(formData.price);
    const durationNum = Number(formData.duration_days);
    const priceProblem = priceNum === null || Number.isNaN(priceNum)
        ? 'Enter a price (0 for a free tier).'
        //old : priceNum < 0 ? "Price can't be negative." : '';
        : priceNum < 0 ? "Price can't be negative."
            // dollars and cents, as the backend saves it (9.125 was shown $9.13 and billed $9.12 in Stripe)
            : Math.round(priceNum * 100) / 100 !== priceNum ? 'Use dollars and cents (2 decimals at most).' : '';
    const durationProblem = formData.duration_days === '' || !Number.isInteger(durationNum) || durationNum < 1
        ? 'Enter how many days a membership lasts (1 or more).' : '';
    const trialProblem = formData.trial_days !== '' && Number(formData.trial_days) < 0 ? "Trial days can't be negative." : '';
    const isFreeForever = priceNum === 0 && !formData.trial_days;
    // What a blank limit box gives: the backend's default (3 a month on a free plan, 30 on a paid one;
    // CSI retakes 3 on either), or the number saved before when the box was cleared
    const limitPlaceholder = (f) => (savedLimits[f.key] != null
        ? `${savedLimits[f.key]} (saved)` : `${isFreeForever ? 3 : (f.paidDefault ?? 30)} (default)`);
    const priceIsZero = priceNum === 0;
    const activeTypes = (types || []).filter(t => t.isActive || audienceIds.includes(t.MembershipTypeID));
    // Audience names this tier targets — drives which usage limits are editable.
    const audienceNames = (types || [])
        .filter((t) => audienceIds.includes(t.MembershipTypeID))
        .map((t) => t.MembershipTypeName);
    // A friend referral gives both students a week of this tier, so it has to
    // be an active, paid tier open to Students (the server checks the same)
    const rewardBlockedReason = !formData.is_active
        ? 'Only an active tier can be the referral reward.'
        : !audienceNames.includes('Student')
            //old ? 'Pick Student under “Available to” to use this tier as the referral reward.'
            ? 'Pick Client under “Available to” to use this tier as the referral reward.'
            : !(priceNum > 0)
                ? "A free tier can't be the referral reward."
                : '';

    const toggleAudience = (id) => {
        setAudienceIds((prev) => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };
    const toggleFeature = (id) => {
        setFeatureIds((prev) => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    // Group features by category for the "Unlocks features" picker.
    // Only the on/off features of the audiences this tier is for (a Student tier: CSI Detailed
    // Recommendations; a Coach tier: Coach Reporting, My Program; an Enterprise tier, which an
    // organization can give its students or its coaches: both), and any the tier already unlocks,
    // so one that's on can always be seen and turned off
    const typeNames = (types || []).map((t) => t.MembershipTypeName);
    const savedFeatureIds = tier?.FeatureIds || [];
    const shownFeature = (f) => !LIMIT_ONLY_KEYS.includes(f.FeatureKey)
        && (audienceNames.includes(f.Category) || audienceNames.includes('Enterprise') || !typeNames.includes(f.Category)
            || savedFeatureIds.includes(f.FeatureID));
    const featureGroups = {};
    //old (features || []).forEach((f) => {
    (features || []).filter(shownFeature).forEach((f) => {
        const cat = f.Category || 'General';
        (featureGroups[cat] = featureGroups[cat] || []).push(f);
    });
    // Match the Usage limits order below, so the two lists line up.
    Object.values(featureGroups).forEach((list) =>
        list.sort((a, b) => featureRank(a.FeatureKey) - featureRank(b.FeatureKey))
    );

    const sellsToOrgs = audienceNames.includes('Student') || audienceNames.includes('Coach');
    const planPrice = priceNum || 0;
    // autoPrices: a monthly or yearly tier's Stripe prices are made when it's saved (an old one-time tier's are still typed)
    const priceMadeOnSave = Boolean(autoPrices) && ['monthly', 'yearly'].includes(formData.billing_frequency);
    const linkCls = `font-semibold hover:underline ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`;
    // The tier's own Stripe price as Edit Tier says it: the one saved while the price and billing are as saved,
    // else made in Stripe when the tier is saved (a tier saved inactive, or free with no trial, gets none, so the
    // one it has may be for a price it had before)
    const perText = formData.billing_frequency === 'yearly' ? 'a year' : 'a month';
    const savedBilling = Boolean(tier?.TierID) && Boolean(tier.IsActive) && Number(tier.Price) === planPrice
        && (tier.BillingFrequency || 'monthly') === formData.billing_frequency
        && (Number(tier.Price) > 0 || Number(tier.TrialDays) > 0);
    const savedPriceId = (tier?.PaymentLink || '').trim();
    const priceIdKept = savedBilling && savedPriceId.startsWith('price_') && formData.payment_link.trim() === savedPriceId;
    const stripePriceText = isFreeForever ? 'Not needed for a free tier.'
        : priceIdKept ? <><span className="font-mono text-xs break-all">{savedPriceId}</span> (${planPrice.toFixed(2)} {perText}).</>
            : !formData.is_active ? 'It\'s made in Stripe when the tier is active and saved.'
                : `Made in Stripe when you save: $${planPrice.toFixed(2)} ${perText}.`
                    + (savedPriceId.startsWith('price_') ? ' People already paying keep the price they have.' : '');
    // A discount's Stripe price: the one saved for it while its price is as saved, else made when the tier is saved
    // (only while it was saved sold to organizations: a discount kept with Group pricing off isn't priced again)
    const stepPriceText = (d) => {
        const saved = (tier?.GroupDiscounts || []).find((s) => Number(s.MinQuantity) === Number(d.min_quantity));
        const kept = saved && savedBilling && Boolean(tier.SoldToOrganizations) && Math.abs(Number(saved.PercentOff) - d.pct) < 0.0001 && saved.StripePriceID
            && saved.StripePriceID === (d.stripe_price_id || '').trim();
        return kept ? <>Stripe price: <span className="font-mono break-all">{saved.StripePriceID}</span></> : 'Stripe price made when you save';
    };
    // Free and one-time tiers can't be sold to organizations: seats are billed
    // on the tier's own cycle and renew automatically.
    // The section is always shown so admins can find it; when a tier can't be
    // sold to organizations the switch is off and says why.
    //const orgBlockedReason = !sellsToOrgs
    const orgBlockedReason = !formData.is_active
        ? "Inactive tiers can't be sold to organizations."
        : !sellsToOrgs
        //old ? "Pick Student or Coach under “Available to” first."
        ? "Pick Client or Coach under “Available to” first."
        : planPrice <= 0
            ? "Free tiers can't be sold to organizations."
            : formData.billing_frequency === 'one-time'
                //old ? "Only monthly or yearly tiers can be sold to organizations (seats renew automatically)."
                ? "Only monthly or yearly tiers can be sold to organizations (licenses renew automatically)."
                //old : !formData.payment_link.trim().startsWith('price_')
                // (made when the tier is saved with autoPrices)
                : !priceMadeOnSave && !formData.payment_link.trim().startsWith('price_')
                    //old ? "This tier needs a Stripe Price ID (price_...) first: seats without a discount are paid with it."
                    ? "This tier needs a Stripe Price ID (price_...) first: licenses without a discount are paid with it."
                    : '';
    const groupOn = group.enabled && !orgBlockedReason;
    const perPeriod = formData.billing_frequency === 'yearly' ? 'year' : 'month';
    const priceAfter = (pct) => seatPrice(planPrice, pct);
    const pctText = (pct) => String(Math.round(pct * 100) / 100);
    // A row the admin touched must be valid; a fully blank row is just ignored.
    const isBlankRow = (d) => d.min_quantity === '' && !d.pct && !d.pctText && !d.priceText && !d.stripe_price_id;
    const discountProblems = group.discounts.map((d, i) => {
        if (isBlankRow(d)) return '';
        const min = Number(d.min_quantity);
        if (d.min_quantity === '' || !Number.isInteger(min) || min < 2) return 'Start at 2 or more licenses.';
        if (group.discounts.some((o, j) => j < i && Number(o.min_quantity) === min)) return 'Another discount already starts at this number.';
        if (!(d.pct > 0 && d.pct < 100)) return `Use more than 0% and less than 100% off (a price between $0 and $${planPrice.toFixed(2)}).`;
        // Buying more never costs more per license
        const smaller = group.discounts.filter((o) => !isBlankRow(o) && Number(o.min_quantity) < min && o.pct >= d.pct)
            .sort((x, y) => Number(y.min_quantity) - Number(x.min_quantity))[0];
        if (smaller) return `Give more than the ${smaller.min_quantity}+ discount (${pctText(smaller.pct)}% off).`;
        //old if (!(d.stripe_price_id || '').trim().startsWith('price_')) return `Add the Stripe Price ID for this price ($${priceAfter(d.pct).toFixed(2)} per ${perPeriod}).`;
        // (made when the tier is saved with autoPrices: then only an ID typed in its box has to be one)
        const stepId = (d.stripe_price_id || '').trim();
        if (priceMadeOnSave ? stepId && !stepId.startsWith('price_') : !stepId.startsWith('price_')) {
            return priceMadeOnSave ? 'A Stripe Price ID starts with price_. Clear the box to have it made when you save.'
                : `Add the Stripe Price ID for this price ($${priceAfter(d.pct).toFixed(2)} per ${perPeriod}).`;
        }
        return '';
    });
    const groupDiscounts = group.discounts.filter((d) => !isBlankRow(d));
    // What each Community Partner package costs with the price and discounts as they stand
    const pctAt = (qty) => groupDiscounts.filter((d) => qty >= Number(d.min_quantity))
        .reduce((best, d) => (Number(d.min_quantity) > best.min ? { min: Number(d.min_quantity), pct: d.pct } : best), { min: 0, pct: 0 }).pct;
    const packages = PARTNER_PAID.map((paid) => {
        const each = seatPrice(planPrice, pctAt(paid));
        return { paid, licenses: PARTNER_FREE + paid, each, total: seatTotal(each, paid) };
    });
    const derivedPackage = (i) => (planPrice > 0 ? packages[i].total.toFixed(2) : '');
    const packageShown = { p50: packageEdit.p50 ?? derivedPackage(0), p100: packageEdit.p100 ?? derivedPackage(1) };
    //old // A discount from 25 licenses or fewer also prices the 50-license package, so the boxes can't set it
    //old const lowStep = groupDiscounts.find((d) => Number(d.min_quantity) <= PARTNER_PAID[0] && d.pct > 0);
    // The package box is this plan's only price editor (its Price and discount rows aren't shown), so a
    // discount it doesn't make (anything but the one from 75 paid licenses) is replaced when a package is typed
    const otherSteps = groupDiscounts.filter((d) => Number(d.min_quantity) !== PARTNER_PAID[1]);
    // How the package prices are saved: the price per license and each discount step. With Group pricing off
    // Save doesn't send the discounts and the ones saved before stay (the backend keeps them), so those are listed
    const savedSteps = groupOn ? groupDiscounts
        : (tier?.GroupDiscounts || []).map((d) => ({ min_quantity: d.MinQuantity, pct: d.PercentOff }));
    const stepsKey = (list) => list.map((d) => `${Number(d.min_quantity)}:${Math.round(d.pct * 10000)}`).sort().join();
    const stepsNotSaved = stepsKey(savedSteps) !== stepsKey(groupDiscounts);
    const savedAs = [`$${planPrice.toFixed(2)} per license`, ...[...savedSteps]
        .sort((a, b) => Number(a.min_quantity) - Number(b.min_quantity))
        .map((d) => `$${priceAfter(d.pct).toFixed(2)} each from ${d.min_quantity} paid licenses`)].join(', ');
    // In the Community Partner box a plan without its package prices yet says to type them
    const partnerBlockedReason = orgBlockedReason && planPrice <= 0 && formData.is_active && sellsToOrgs
        ? 'Type the 50 and 100 license prices first.' : orgBlockedReason;
    const packageProblem = (key, paid) => {
        if (!Object.keys(packageEdit).length) return ''; // both shown from Price and the discounts
        const text = String(packageShown[key]).trim();
        if (text === '') return `Enter the price for ${PARTNER_FREE + paid} licenses.`;
        const cents = toCents(text);
        if (!(cents > 0)) return 'Enter a price above $0.';
        if (cents % paid) {
            return `Doesn't split evenly over the ${paid} paid licenses. Try ${centsText(Math.floor(cents / paid) * paid)} or ${centsText(Math.ceil(cents / paid) * paid)}.`;
        }
        const c50 = toCents(packageShown.p50);
        if (key === 'p100' && c50 > 0 && !(c50 % PARTNER_PAID[0]) && cents / paid > c50 / PARTNER_PAID[0]) {
            return `100 licenses can't cost more per license than 50 (${centsText(cents / paid)} vs ${centsText(c50 / PARTNER_PAID[0])} each).`;
        }
        return '';
    };
    const p50Problem = packageProblem('p50', PARTNER_PAID[0]);
    const p100Problem = packageProblem('p100', PARTNER_PAID[1]);
    const d75 = group.discounts.findIndex((d) => Number(d.min_quantity) === PARTNER_PAID[1]);
    // The Community Partner box's Stripe prices (autoPrices): the plan's and the 75+ discount's as saved, else made on save
    // (the 75+ one only while it was saved with Group pricing on, like the discount rows)
    const step75Saved = (tier?.GroupDiscounts || []).find((s) => Number(s.MinQuantity) === PARTNER_PAID[1]);
    const cpIdsKept = priceIdKept && (d75 < 0 || Boolean(tier?.SoldToOrganizations && step75Saved && step75Saved.StripePriceID
        && Math.abs(Number(step75Saved.PercentOff) - group.discounts[d75].pct) < 0.0001
        && step75Saved.StripePriceID === (group.discounts[d75].stripe_price_id || '').trim()));
    const cpPriceText = cpIdsKept
        ? <>Stripe prices: <span className="font-mono break-all">{savedPriceId}</span> (${planPrice.toFixed(2)} each){d75 >= 0 && (
            <> and <span className="font-mono break-all">{step75Saved.StripePriceID}</span> (${seatPrice(planPrice, group.discounts[d75].pct).toFixed(2)} each)</>
        )}.</>
        : groupOn ? 'Their Stripe prices are made when you save.' : 'Their Stripe prices are made when you save with Group pricing on.';
    // Typing a package price writes Price and the discounts as soon as the two boxes make sense together;
    // then only the box being typed in keeps its own text, so neither can show an old price
    const setPackage = (key, value) => {
        const edit = { ...packageEdit, [key]: value };
        const c50 = toCents(edit.p50 ?? derivedPackage(0));
        const c100 = toCents(edit.p100 ?? derivedPackage(1));
        const ok = c50 > 0 && !(c50 % PARTNER_PAID[0]) && c100 > 0 && !(c100 % PARTNER_PAID[1])
            && c100 / PARTNER_PAID[1] <= c50 / PARTNER_PAID[0];
        setPackageEdit(ok ? { [key]: value } : edit);
        if (!ok) return;
        const unit = c50 / PARTNER_PAID[0];
        const each = c100 / PARTNER_PAID[1];
        setFormData((f) => ({ ...f, price: (unit / 100).toFixed(2) }));
        setGroup((g) => {
            const kept = g.discounts.find((d) => Number(d.min_quantity) === PARTNER_PAID[1]);
            return {
                ...g,
                //old enabled: true,
                // a new plan's package prices turn Group pricing on until the admin turns it off here (also when its
                // price was typed before ticking the plan); a saved plan's stays as the admin set it (off is a choice:
                // the sign-up page says online payment opens soon)
                enabled: g.enabled || (!tier?.TierID && !g.chosen),
                // the packages set the discounts: one from 75 licenses, or none when 100 costs the same per license
                discounts: each < unit
                    ? [{ min_quantity: PARTNER_PAID[1], pct: (1 - each / unit) * 100, stripe_price_id: kept?.stripe_price_id || '' }]
                    : [],
            };
        });
    };
    // Leaving a box whose price was written shows it from Price and the discounts again
    const endPackageEdit = (key, i) => {
        if (key in packageEdit && toCents(packageEdit[key]) === Math.round(packages[i].total * 100)) {
            setPackageEdit((e) => {
                const next = { ...e };
                delete next[key];
                return next;
            });
        }
    };
    // Example for the preview line: the biggest discount step, or 10 licenses.
    const exampleQty = groupDiscounts.length ? Math.max(...groupDiscounts.map((d) => Number(d.min_quantity))) : 10;
    const examplePct = groupDiscounts.filter((d) => exampleQty >= Number(d.min_quantity))
        .reduce((best, d) => (Number(d.min_quantity) > best.min ? { min: Number(d.min_quantity), pct: d.pct } : best), { min: 0, pct: 0 }).pct;
    const exampleTotal = seatTotal(seatPrice(planPrice, examplePct), exampleQty);

    const submit = () => onSave({
        ...formData,
        price: priceNum,
        duration_days: durationNum,
        payment_link: formData.payment_link.trim(),
        membership_type_ids: audienceIds,
        //old feature_ids: featureIds,
        // a feature the tier already unlocks stays as it was (the picker shows it, or its limit is what counts),
        // so Save without a change changes nothing; one ticked here and then hidden by a change under
        // Available to comes off; if the features didn't load, nothing changes
        feature_ids: featureIds.filter((id) => savedFeatureIds.includes(id)
            || !(features || []).some((f) => f.FeatureID === id && !shownFeature(f))),
        sold_to_organizations: groupOn,
        group_discounts: groupOn ? groupDiscounts.map((d) => ({
            min_quantity: Number(d.min_quantity), percent_off: Math.round(d.pct * 10000) / 10000,
            stripe_price_id: (d.stripe_price_id || '').trim(),
        })) : [],
        // Only send limits for audiences this tier actually targets — a Coach-only
        // tier has no business writing Student feature quotas (that's how a stray
        // Mock Interview limit ended up on a coach tier).
        feature_limits: FEATURE_LIMIT_DEFS.filter((f) => audienceNames.includes(f.audience)).map((f) => ({
            feature: f.key, value: limits[f.key], id: limitIds[f.key] || null,
        })),
    });

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Tier Name *</label>
                    <input
                        type="text"
                        value={formData.tier_name}
                        onChange={(e) => setFormData({ ...formData, tier_name: e.target.value })}
                        className={`w-full p-2 border rounded-lg ${isDarkMode ? 'bg-slate-900 border-slate-600 text-slate-200' : 'border-slate-300'}`}
                        //old placeholder="e.g., Coach Pro, Student Pro"
                        placeholder="e.g., Coach Pro, Client Pro"
                    />
                </div>
                <div>
                    <label className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Status</label>
                    <select
                        value={formData.is_active}
                        onChange={(e) => setFormData({ ...formData, is_active: parseInt(e.target.value) })}
                        className={`select-styled w-full text-sm font-medium rounded-xl px-3 py-2.5 border shadow-sm ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-200 hover:border-slate-500' : 'border-slate-300 bg-white text-slate-700 hover:border-slate-400'}`}
                    >
                        <option value={1}>Active</option>
                        <option value={0}>Inactive</option>
                    </select>
                    {/* Organizations sponsor this tier: say what turning it off does to them */}
                    {!formData.is_active && Boolean(tier?.SoldToOrganizations) && (
                        <p className={`mt-1 text-xs ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                            {/* old Organizations buy seats of this tier. Once it's inactive they can't buy more; */}
                            {/* old seats they already have keep this tier. */}
                            Organizations buy licenses of this tier. Once it's inactive they can't buy more;
                            licenses they already have keep this tier.
                        </p>
                    )}
                </div>
            </div>

            {/* Audience — any combination of Student / Coach / Enterprise */}
            <div>
                <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Available to *</label>
                <div className="flex flex-wrap gap-2">
                    {activeTypes.map((t) => {
                        const on = audienceIds.includes(t.MembershipTypeID);
                        return (
                            <button
                                type="button"
                                key={t.MembershipTypeID}
                                onClick={() => toggleAudience(t.MembershipTypeID)}
                                className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors ${
                                    on
                                        ? 'bg-blue-600 border-blue-600 text-white'
                                        : isDarkMode ? 'border-slate-600 text-slate-300 hover:border-slate-500' : 'border-slate-300 text-slate-600 hover:border-slate-400'
                                }`}
                            >
                                {/* old {on ? '✓ ' : ''}{t.MembershipTypeName} */}
                                {on ? '✓ ' : ''}{AUDIENCE_LABEL[t.MembershipTypeName] || t.MembershipTypeName}
                            </button>
                        );
                    })}
                    {activeTypes.length === 0 && (
                        <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>No audience types available.</span>
                    )}
                </div>
                {/* old <p className={`mt-1 text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Pick one or more. Students see Student tiers, coaches see Coach tiers.</p> */}
                <p className={`mt-1 text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Pick one or more. Clients see Client tiers, coaches see Coach tiers.</p>
            </div>

            {/* Feature Access — which features this tier unlocks, grouped by category */}
            <div>
                <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Unlocks features</label>
                {Object.keys(featureGroups).length === 0 ? (
                    //old <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>No features defined yet.</span>
                    <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        {!(features || []).length ? 'No features defined yet.'
                            : audienceIds.length ? 'None for this audience.' : 'Pick an audience under “Available to” first.'}
                    </span>
                ) : (
                    <div className="space-y-2.5">
                        {Object.keys(featureGroups).sort().map((cat) => (
                            <div key={cat}>
                                {/* old <div className={`text-xs font-semibold uppercase tracking-wide mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{cat}</div> */}
                                <div className={`text-xs font-semibold uppercase tracking-wide mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{AUDIENCE_LABEL[cat] || cat}</div>
                                <div className="flex flex-wrap gap-2">
                                    {featureGroups[cat].map((f) => {
                                        const on = featureIds.includes(f.FeatureID);
                                        return (
                                            <button
                                                type="button"
                                                key={f.FeatureID}
                                                onClick={() => toggleFeature(f.FeatureID)}
                                                title={f.Description || ''}
                                                className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors ${
                                                    on
                                                        ? 'bg-primary-600 border-primary-600 text-white'
                                                        : isDarkMode ? 'border-slate-600 text-slate-300 hover:border-slate-500' : 'border-slate-300 text-slate-600 hover:border-slate-400'
                                                }`}
                                            >
                                                {on ? '✓ ' : ''}{f.DisplayName}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                {/* old <p className={`mt-1 text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Turn on the features members on this tier can use.</p> */}
                <p className={`mt-1 text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    Turn on the features members on this tier can use.
                    {audienceNames.includes('Student') && ' Resume Analysis, Mock Interview and Elevator Pitch are set by their limits below.'}
                </p>
            </div>

            {/* Usage Limits — per-feature monthly quota, grouped by audience the same
                way "Unlocks features" is, so a tier only fills in the limits that
                apply to who it's sold to. A group the tier isn't available to is
                greyed out and disabled. */}
            <div>
                <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    {/* old Usage limits <span className="font-normal text-xs opacity-70">(per month · blank = unchanged)</span> */}
                    Usage limits <span className="font-normal text-xs opacity-70">(per month · blank gives the number shown)</span>
                </label>
                <div className="space-y-2.5">
                    {[...new Set(FEATURE_LIMIT_DEFS.map((f) => f.audience))].sort().map((aud) => {
                        const defs = FEATURE_LIMIT_DEFS.filter((f) => f.audience === aud);
                        // Some features are access-gated rather than metered — opening a
                        // report or a settings page isn't a countable "use" — so their
                        // fields stay read-only no matter which audience the tier targets.
                        const metered = defs.some((f) => f.metered !== false);
                        const applies = metered && audienceNames.includes(aud);
                        const note = !metered
                            ? '— on/off only, set under Unlocks features'
                            : (!applies ? '— not available to this tier' : '');
                        return (
                            <div key={aud}>
                                <div className={`text-xs font-semibold uppercase tracking-wide mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                    {/* old {aud} */}
                                    {AUDIENCE_LABEL[aud] || aud}
                                    {note && (
                                        <span className="ml-2 font-normal normal-case opacity-70">{note}</span>
                                    )}
                                </div>
                                <div className={`grid grid-cols-1 md:grid-cols-2 gap-3 ${applies ? '' : 'opacity-50'}`}>
                                    {defs.map((f) => (
                                        <div key={f.key}>
                                            <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{f.label}</label>
                                            <input
                                                type="number"
                                                min="0"
                                                disabled={!applies}
                                                value={applies ? limits[f.key] : ''}
                                                onChange={(e) => setLimits({ ...limits, [f.key]: e.target.value })}
                                                className={`w-full p-2 border rounded-lg ${isDarkMode ? 'bg-slate-900 border-slate-600 text-slate-200' : 'border-slate-300'} ${applies ? '' : 'cursor-not-allowed'}`}
                                                //old placeholder={applies ? 'e.g. 3' : '—'}
                                                placeholder={applies ? limitPlaceholder(f) : '—'}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    {/* old <label className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Duration (days)</label> */}
                    <label className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Days given free</label>
                    <input
                        type="number"
                        value={formData.duration_days}
                        min="1"
                        onChange={(e) => setFormData({ ...formData, duration_days: e.target.value })}
                        className={`w-full p-2 border rounded-lg ${isDarkMode ? 'bg-slate-900 border-slate-600 text-slate-200' : 'border-slate-300'}`}
                    />
                    {durationProblem && <p className="mt-1 text-xs font-medium text-red-500">{durationProblem}</p>}
                    {/* DurationDays: a free code's grant, a switch to a free plan, and a paid plan whose Stripe period can't be read */}
                    <p className={`mt-1 text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        How long the plan lasts when no card pays for it: a free promo code, a free plan, or when Stripe can't be read.
                        Plans paid by card follow their billing period.
                    </p>
                </div>
                <div>
                    <label className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                        Trial Days <span className="font-normal text-xs opacity-70">(e.g. Beta: 90)</span>
                    </label>
                    <input
                        type="number"
                        value={formData.trial_days}
                        min="0"
                        onChange={(e) => setFormData({ ...formData, trial_days: e.target.value ? parseInt(e.target.value) : '' })}
                        className={`w-full p-2 border rounded-lg ${isDarkMode ? 'bg-slate-900 border-slate-600 text-slate-200' : 'border-slate-300'}`}
                        placeholder="Leave blank for no trial"
                    />
                    {trialProblem && <p className="mt-1 text-xs font-medium text-red-500">{trialProblem}</p>}
                    {/* A trial needs a Stripe price, and the Community Partner plan's box asks for it once the packages are priced */}
                    {/* (with autoPrices nothing is typed for it: it's made when the tier is saved) */}
                    {formData.is_community_partner_tier && Number(formData.trial_days) > 0 && planPrice <= 0 && !formData.payment_link.trim() && !priceMadeOnSave && (
                        <p className="mt-1 text-xs font-medium text-red-500">
                            A trial needs a paid plan: type the 50 and 100 license prices below, or leave Trial Days blank.
                        </p>
                    )}
                </div>
                {/* The Community Partner plan's price, billing and Stripe prices are set in its package box below, one place for them */}
                {!formData.is_community_partner_tier && (<>
                <div>
                    <label className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Price ($)</label>
                    <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.price}
                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                        className={`w-full p-2 border rounded-lg ${isDarkMode ? 'bg-slate-900 border-slate-600 text-slate-200' : 'border-slate-300'}`}
                    />
                    {priceProblem && <p className="mt-1 text-xs font-medium text-red-500">{priceProblem}</p>}
                </div>
                <div>
                    <label className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Billing Frequency</label>
                    <select
                        value={formData.billing_frequency}
                        onChange={(e) => setFormData({ ...formData, billing_frequency: e.target.value })}
                        disabled={priceIsZero}
                        className={`select-styled w-full text-sm font-medium rounded-xl px-3 py-2.5 border shadow-sm ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-200 hover:border-slate-500' : 'border-slate-300 bg-white text-slate-700 hover:border-slate-400'} ${priceIsZero ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        <option value="monthly">Monthly</option>
                        <option value="yearly">Yearly</option>
                        {/* <option value="one-time">One-time</option> */}
                        {/* Checkout only sells subscriptions; One-time only shows on an old tier that has it */}
                        {formData.billing_frequency === 'one-time' && <option value="one-time">One-time</option>}
                    </select>
                </div>
                {/* autoPrices (JJ, Oct 28): the Stripe price is made when the tier is saved, so this says which one is used,
                    and the box is only to use a Price ID made in Stripe instead */}
                {priceMadeOnSave && !ownIds.base ? (
                    <div className="md:col-span-2">
                        <span className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Stripe price</span>
                        <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            {stripePriceText}
                            {!isFreeForever && <>{' '}<button type="button" className={linkCls} onClick={() => setOwnIds((o) => ({ ...o, base: true }))}>Use a Price ID from Stripe instead</button></>}
                        </p>
                    </div>
                ) : (
                <div className="md:col-span-2">
                    <label className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                        {/* old Price ID {isFreeForever ? '' : '*'} */}
                        Price ID {isFreeForever || priceMadeOnSave ? '' : '*'}
                    </label>
                    <input
                        type="text"
                        value={formData.payment_link}
                        onChange={(e) => setFormData({ ...formData, payment_link: e.target.value })}
                        className={`w-full p-2 border rounded-lg ${isDarkMode ? 'bg-slate-900 border-slate-600 text-slate-200' : 'border-slate-300'}`}
                        placeholder={isFreeForever ? 'Not needed for a free tier' : 'price_1A2b3C...'}
                    />
                    {priceMadeOnSave && (
                        <p className={`mt-1 text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            A Price ID made in Stripe for ${planPrice.toFixed(2)} {perText}, per license. Leave it empty to have it made when you save.
                        </p>
                    )}
                </div>
                )}
                </>)}
            </div>
            <div>
                <label className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Description</label>
                <textarea
                    value={formData.tier_description}
                    onChange={(e) => setFormData({ ...formData, tier_description: e.target.value })}
                    className={`w-full p-2 border rounded-lg ${isDarkMode ? 'bg-slate-900 border-slate-600 text-slate-200' : 'border-slate-300'}`}
                    rows="3"
                    placeholder="Describe this membership tier..."
                />
            </div>

            {/* Group pricing — organizations whose licenses grant this tier can buy
                more seats at this price (request -> Pono Pilot approval -> payment). */}
            {/* Sell to organizations (group pricing) */}
            {!formData.is_community_partner_tier && (
            <div className={`rounded-xl border p-4 ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                <label className={`flex items-center gap-2 text-sm font-semibold ${orgBlockedReason ? 'opacity-50' : ''} ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    <input type="checkbox" checked={groupOn} disabled={!!orgBlockedReason}
                           onChange={(e) => setGroup({ ...group, enabled: e.target.checked })} />
                    {/* old <Building2 size={16} /> Sell to organizations */}
                    <Building2 size={16} /> <span>Group pricing <span className="font-normal text-xs opacity-70">(sell licenses to organizations)</span></span>
                </label>
                <p className={`mt-1 ml-6 text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    {orgBlockedReason || (
                        <>
                        {/* old <>Organizations whose licenses give this tier pay for the seats you set up for them (Organizations → License requests). */}
                        {/* old Organizations whose licenses give this tier pay for the seats you set up for them (Organizations → Licenses on the organization's row). */}
                        Organizations whose licenses give this tier pay for the licenses you set up for them (Organizations → Licenses on the organization's row).
                        {/* old {' '}Each seat costs this tier's price (<span className="font-semibold">${planPrice.toFixed(2)} / {perPeriod}</span>), less a volume discount.</> */}
                        {' '}Each license costs this tier's price (<span className="font-semibold">${planPrice.toFixed(2)} / {perPeriod}</span>), less a volume discount.</>
                    )}
                </p>
                {groupOn && (
                    <div className="mt-4 space-y-4">
                        <div>
                            <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                Volume discounts <span className="font-normal text-xs opacity-70">(buy more, pay less)</span>
                            </label>
                            <div className="space-y-2">
                                {group.discounts.map((d, i) => (
                                    <div key={i} className={`flex flex-wrap items-center gap-2 text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                                        <input type="number" min="2" step="1" value={d.min_quantity} aria-label="Minimum licenses" placeholder="10"
                                               onChange={(e) => setDiscount(i, { min_quantity: e.target.value })} className={boxCls("w-16")} />
                                        <span className="whitespace-nowrap">+ licenses →</span>
                                        <input type="number" min="0" max="99.99" step="0.01" aria-label="Percent off" placeholder="10"
                                               value={d.pctText ?? (d.pct ? pctText(d.pct) : '')}
                                               onChange={(e) => {
                                                   const pct = parseFloat(e.target.value) || 0;
                                                   setDiscount(i, { pct, pctText: e.target.value, priceText: undefined });
                                               }}
                                               className={boxCls("w-20")} />
                                        <span className="whitespace-nowrap">% off</span>
                                        <span className={isDarkMode ? 'text-slate-500' : 'text-slate-400'}>or</span>
                                        <span>$</span>
                                        <input type="number" min="0" step="0.01" aria-label="Price per license after the discount"
                                               placeholder={planPrice ? priceAfter(10).toFixed(2) : ''}
                                               value={d.priceText ?? (d.pct ? priceAfter(d.pct).toFixed(2) : '')}
                                               onChange={(e) => {
                                                   const each = parseFloat(e.target.value);
                                                   const pct = planPrice && !Number.isNaN(each) ? (1 - each / planPrice) * 100 : 0;
                                                   setDiscount(i, { pct, priceText: e.target.value, pctText: undefined });
                                               }}
                                               className={boxCls("w-24")} />
                                        <span>each</span>
                                        {/* (autoPrices: its Stripe price is made when the tier is saved, the box only for one made in Stripe) */}
                                        {priceMadeOnSave && !ownIds.steps ? (
                                            <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{stepPriceText(d)}</span>
                                        ) : (
                                        <input type="text" aria-label="Stripe Price ID for this price" placeholder="Stripe Price ID (price_...)"
                                               value={d.stripe_price_id || ''}
                                               onChange={(e) => setDiscount(i, { stripe_price_id: e.target.value })}
                                               className={`${boxCls("w-56")} font-mono text-xs`} />
                                        )}
                                        <button type="button" aria-label="Remove discount"
                                                onClick={() => setGroup((g) => ({ ...g, discounts: g.discounts.filter((_, j) => j !== i) }))}
                                                className={`p-1.5 rounded-lg ${isDarkMode ? 'text-slate-400 hover:text-red-400 hover:bg-slate-700' : 'text-slate-500 hover:text-red-600 hover:bg-red-50'}`}>
                                            <Trash2 size={16} />
                                        </button>
                                        {discountProblems[i] && <p className="basis-full text-xs font-medium text-red-500">{discountProblems[i]}</p>}
                                    </div>
                                ))}
                            </div>
                            <button type="button" onClick={() => setGroup((g) => ({ ...g, discounts: [...g.discounts, { min_quantity: '', pct: 0, stripe_price_id: '' }] }))}
                                    className={`mt-2 inline-flex items-center gap-1 text-sm font-semibold ${isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}>
                                <Plus size={14} /> Add discount
                            </button>
                            <p className={`mt-1 text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                Type a percent off or the price each; the other box follows. An order gets the biggest discount it qualifies for.
                                Example: with 10+ = 10% off and 50+ = 20% off, 30 licenses get 10% off.
                                {!(priceMadeOnSave && !ownIds.steps) && (<>{' '}
                                Each discounted price needs its own Stripe price: create one in Stripe for that price per license (same billing period) and paste its ID.
                                {priceMadeOnSave && ' Or leave its box empty to have it made when you save.'}
                                </>)}
                                {/* old Seats without a discount use this tier's Price ID. */}
                                {' '}Licenses without a discount use this tier's {priceMadeOnSave ? 'Stripe price' : 'Price ID'}.
                                {/* autoPrices: each discounted price's Stripe price is made when the tier is saved */}
                                {priceMadeOnSave && !ownIds.steps && (<>
                                    {' '}Each discounted price gets its own Stripe price when you save.{' '}
                                    <button type="button" className={linkCls} onClick={() => setOwnIds((o) => ({ ...o, steps: true }))}>Use Price IDs from Stripe instead</button>
                                </>)}
                            </p>
                        </div>
                        <p className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                            Example: {exampleQty} licenses = <span className="font-semibold">${exampleTotal.toFixed(2)}</span> per {perPeriod}
                            {examplePct > 0 ? ` (${pctText(examplePct)}% off)` : ''}.
                        </p>
                    </div>
                )}
            </div>
            )}
            <div className="flex flex-wrap gap-6">
                <label className={`flex items-center gap-2 text-sm font-semibold ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    <input
                        type="checkbox"
                        checked={formData.show_in_onboarding}
                        onChange={(e) => setFormData({ ...formData, show_in_onboarding: e.target.checked })}
                        className="w-4 h-4"
                    />
                    Show in onboarding
                </label>
                <label className={`flex items-center gap-2 text-sm font-semibold ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    <input
                        type="checkbox"
                        checked={formData.open_for_enrollment}
                        onChange={(e) => setFormData({ ...formData, open_for_enrollment: e.target.checked })}
                        className="w-4 h-4"
                    />
                    Open for new enrollment
                    <span className="font-normal text-xs opacity-70">(turn off to stop new signups without affecting existing members)</span>
                </label>
                <label className={`flex items-center gap-2 text-sm font-semibold ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    <input
                        type="checkbox"
                        checked={formData.is_most_popular}
                        onChange={(e) => setFormData({ ...formData, is_most_popular: e.target.checked })}
                        className="w-4 h-4"
                    />
                    Mark as “Most Popular”
                    <span className="font-normal text-xs opacity-70">(highlights this tier + shows the badge on the plans page)</span>
                </label>
                <label className={`flex items-center gap-2 text-sm font-semibold ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    <input
                        type="checkbox"
                        checked={formData.is_referral_reward}
                        onChange={(e) => setFormData({ ...formData, is_referral_reward: e.target.checked })}
                        disabled={!formData.is_referral_reward && Boolean(rewardBlockedReason)}
                        className="w-4 h-4"
                    />
                    Referral reward tier
                </label>
                {/* Exactly one tier holds this, so say which one has it now —
                    ticking it here moves it off that tier. */}
                <p className={`-mt-1 ml-6 text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    The tier a friend referral grants both people a week of. Only one tier can hold this
                    {rewardHolder ? <> — currently <span className="font-semibold">{rewardHolder.TierName}</span>; ticking this moves it here.</> : '.'}
                </p>
                {rewardBlockedReason && (
                    <p className={`-mt-1 ml-6 text-xs ${formData.is_referral_reward
                        ? (isDarkMode ? 'text-red-300' : 'text-red-600')
                        : (isDarkMode ? 'text-slate-400' : 'text-slate-500')}`}>
                        {rewardBlockedReason}
                    </p>
                )}
                {/* Community Partners (Oct 16): the plan their licensed students get, priced per paid license */}
                <label className={`flex items-center gap-2 text-sm font-semibold ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    <input
                        type="checkbox"
                        checked={formData.is_community_partner_tier}
                        //old onChange={(e) => setFormData({ ...formData, is_community_partner_tier: e.target.checked })}
                        // Alicia's packages are sold to organizations a year at a time (Oct 22): ticking it closes the plan to
                        // students' own sign-up and starts a new or free plan at per year (a paid plan keeps its billing, which
                        // its Stripe price is for); both can be changed again, and unticking it puts them back
                        onChange={(e) => {
                            if (e.target.checked) {
                                setBeforePartner({ open_for_enrollment: formData.open_for_enrollment, billing_frequency: formData.billing_frequency });
                                setFormData({
                                    ...formData, is_community_partner_tier: true, open_for_enrollment: false,
                                    billing_frequency: tier?.TierID && Number(tier.Price) > 0 ? formData.billing_frequency : 'yearly',
                                    // its price is typed in the package box only, so a blank or negative one from the
                                    // Price box (hidden now) starts at 0 instead of keeping Save off unseen
                                    price: priceProblem ? '0' : formData.price,
                                });
                            } else {
                                setFormData({ ...formData, ...beforePartner, is_community_partner_tier: false });
                                setBeforePartner(null);
                            }
                        }}
                        className="w-4 h-4"
                    />
                    Community Partner plan
                </label>
                <p className={`-mt-1 ml-6 text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    {/* old What a Community Partner's licensed students get. Its first 25 licenses are free; this tier's price is */}
                    {/* old each license after that, and with Sell to organizations and a Stripe price it opens the paid packages */}
                    {/* old What a Community Partner's licensed students get. Its first 25 licenses are free; the 50 and 100 license */}
                    What a Community Partner's licensed clients get. Its first 25 licenses are free; the 50 and 100 license
                    packages are priced below, and with Group pricing on and their Stripe prices they're sold
                    {/* old on the Community Partner sign-up page. It must be an active Student tier. Only one tier can hold this */}
                    {" "}on the Community Partner sign-up page. It must be an active Client tier. Only one tier can hold this
                    {partnerHolder ? <> — currently <span className="font-semibold">{partnerHolder.TierName}</span>; ticking this moves it here.</> : '.'}
                </p>
                {/* Package prices (Alicia, Oct 22): typed as the 50- and 100-license prices, saved as the price above
                    and a discount from 75 licenses, with the Stripe prices those need */}
                {formData.is_community_partner_tier && (
                    <div className={`ml-6 space-y-3 rounded-xl border p-3 ${isDarkMode ? 'border-slate-700 bg-slate-900/40' : 'border-slate-200 bg-slate-50'}`}>
                        <p className={`text-sm font-semibold ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                            {/* old Package prices, per {perPeriod} <span className="font-normal text-xs opacity-70">(the first {PARTNER_FREE} licenses are free)</span> */}
                            {/* Billing Frequency is set here for this plan (Alicia's prices are per year) */}
                            <label htmlFor="cp-billing">Package prices, per</label>{' '}
                            <select id="cp-billing" value={formData.billing_frequency}
                                    onChange={(e) => setFormData({ ...formData, billing_frequency: e.target.value })}
                                    className={`select-styled text-sm font-medium rounded-xl px-3 py-1.5 border shadow-sm ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-200' : 'border-slate-300 bg-white text-slate-700'}`}>
                                <option value="yearly">year</option>
                                <option value="monthly">month</option>
                                {formData.billing_frequency === 'one-time' && <option value="one-time">one-time payment</option>}
                            </select>{' '}
                            <span className="font-normal text-xs opacity-70">(the first {PARTNER_FREE} licenses are free)</span>
                        </p>
                        <div className="grid gap-3 sm:grid-cols-2">
                            {[['p50', PARTNER_PAID[0], p50Problem, 0], ['p100', PARTNER_PAID[1], p100Problem, 1]].map(([key, paid, problem, i]) => (
                                <div key={key}>
                                    <label htmlFor={`cp-package-${key}`} className={`block text-xs font-semibold mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                        {PARTNER_FREE + paid} licenses
                                    </label>
                                    <div className="flex items-center gap-1">
                                        <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>$</span>
                                        <input id={`cp-package-${key}`} type="number" min="0" step="0.01" placeholder={key === 'p50' ? '499' : '999'}
                                               //old value={packageShown[key]} readOnly={Boolean(lowStep)} aria-invalid={Boolean(problem)}
                                               value={packageShown[key]} aria-invalid={Boolean(problem)}
                                               onChange={(e) => setPackage(key, e.target.value)} onBlur={() => endPackageEdit(key, i)}
                                               //old className={`${boxCls("w-full")} ${lowStep ? 'opacity-60' : ''}`} />
                                               className={boxCls("w-full")} />
                                    </div>
                                    {problem && <p className="mt-1 text-xs font-medium text-red-500">{problem}</p>}
                                </div>
                            ))}
                        </div>
                        {/* old {lowStep && ( */}
                        {otherSteps.length > 0 && (
                            <p className={`text-xs font-medium ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                                {/* old The {lowStep.min_quantity}+ licenses discount above also lowers the 50-license package. Remove it to type package prices here. */}
                                Typing package prices here replaces the {otherSteps.map((d) => `${d.min_quantity}+`).join(' and ')} licenses
                                discount{otherSteps.length > 1 ? 's' : ''} with one from {PARTNER_PAID[1]} paid licenses.
                            </p>
                        )}
                        {planPrice > 0 && (
                            <div className={`space-y-1 text-xs tabular-nums ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                                {packages.map((p) => (
                                    <div key={p.paid}>
                                        {p.licenses} licenses = {PARTNER_FREE} free + {p.paid} × ${p.each.toFixed(2)} ={' '}
                                        <span className="font-semibold">${p.total.toFixed(2)} a {perPeriod}</span>
                                    </div>
                                ))}
                                {/* what Save stores (and Stripe bills): the price per license and the discount */}
                                <div className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Saved as {savedAs}.</div>
                            </div>
                        )}
                        {/* autoPrices (JJ, Oct 28): its Stripe prices are made when the tier is saved; the boxes only to use ones made in Stripe */}
                        {priceMadeOnSave && !ownIds.cp && planPrice > 0 && (
                            <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                {cpPriceText}{' '}
                                <button type="button" className={linkCls} onClick={() => setOwnIds((o) => ({ ...o, cp: true }))}>Use Price IDs from Stripe instead</button>
                            </p>
                        )}
                        {!(priceMadeOnSave && !ownIds.cp) && (<>
                        {planPrice > 0 && (
                            <div className="grid gap-3 sm:grid-cols-2">
                                <div>
                                    <label htmlFor="cp-price-id" className={`block text-xs font-semibold mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                        {/* old Stripe Price ID for ${planPrice.toFixed(2)} each */}
                                        Stripe Price ID for ${planPrice.toFixed(2)} each{priceMadeOnSave ? '' : ' *'}
                                    </label>
                                    <input id="cp-price-id" type="text" placeholder="price_..." value={formData.payment_link}
                                           onChange={(e) => setFormData({ ...formData, payment_link: e.target.value })} className={`${boxCls("w-full")} font-mono text-xs`} />
                                </div>
                                {d75 >= 0 && (
                                    <div>
                                        <label htmlFor="cp-price-id-75" className={`block text-xs font-semibold mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                            {/* old Stripe Price ID for ${seatPrice(planPrice, group.discounts[d75].pct).toFixed(2)} each */}
                                            Stripe Price ID for ${seatPrice(planPrice, group.discounts[d75].pct).toFixed(2)} each{priceMadeOnSave ? '' : ' *'}
                                        </label>
                                        <input id="cp-price-id-75" type="text" placeholder="price_..." value={group.discounts[d75].stripe_price_id || ''}
                                               onChange={(e) => setDiscount(d75, { stripe_price_id: e.target.value })} className={`${boxCls("w-full")} font-mono text-xs`} />
                                        {groupOn && discountProblems[d75] && <p className="mt-1 text-xs font-medium text-red-500">{discountProblems[d75]}</p>}
                                    </div>
                                )}
                            </div>
                        )}
                        </>)}
                        {/* a discount row this box doesn't show that keeps Save off (one typed before ticking the plan); the 75+
                            one's own line is under its Price ID box, shown only with the boxes (autoPrices) */}
                        {groupOn && discountProblems.map((problem, i) => (problem && (i !== d75 || (priceMadeOnSave && !ownIds.cp)) ? (
                            <p key={i} className="text-xs font-medium text-red-500">The {group.discounts[i].min_quantity || 'new'}+ licenses discount: {problem}</p>
                        ) : null))}
                        {/* about the Stripe Price ID boxes, shown once there's a price */}
                        {!(priceMadeOnSave && !ownIds.cp) && (<>
                        {planPrice > 0 && (
                        <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            {/* old Create {d75 >= 0 ? 'each' : 'it'} in Stripe as a price per license, billed every {perPeriod}. These fill in Price and the */}
                            {/* old volume discounts above (Sell to organizations). */}
                            {/* old {orgBlockedReason && <span className={`block mt-1 font-medium ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>{orgBlockedReason}</span>} */}
                            Create {d75 >= 0 ? 'each' : 'it'} in Stripe as a price per license, billed every {perPeriod}.
                            {priceMadeOnSave && ` Or leave ${d75 >= 0 ? 'them' : 'it'} empty to have ${d75 >= 0 ? 'them' : 'it'} made when you save.`}
                        </p>
                        )}
                        </>)}
                        {/* Group pricing sells the paid packages; it isn't forced on: a plan without a price or its
                            Stripe prices yet only gives the 25 free licenses */}
                        <div className={`border-t pt-3 ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                            <label className={`flex items-center gap-2 text-sm font-semibold ${orgBlockedReason ? 'opacity-50' : ''} ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                <input type="checkbox" checked={groupOn} disabled={!!orgBlockedReason}
                                       onChange={(e) => setGroup({ ...group, enabled: e.target.checked, chosen: true })} />
                                <Building2 size={16} /> <span>Group pricing <span className="font-normal text-xs opacity-70">(sell the paid packages)</span></span>
                            </label>
                            <p className={`mt-1 ml-6 text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                Organizations whose licenses give this tier pay for the licenses you set up for them (Organizations → Licenses on the organization's row).
                            </p>
                            {!groupOn && (
                                <p className={`mt-1 ml-6 text-xs font-medium ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                                    {partnerBlockedReason ? `${partnerBlockedReason} ` : ''}While Group pricing is off, the Community Partner sign-up page
                                    shows “Online payment opens soon” for 50 and 100 licenses.
                                    {stepsNotSaved && ' Turn it on to save the 100-license price shown.'}
                                </p>
                            )}
                        </div>
                    </div>
                )}
                {/* Open for new enrollment also lists it on the students' plans page, at this per-license price */}
                {formData.is_community_partner_tier && formData.open_for_enrollment && (
                    <p role="status" className={`-mt-1 ml-6 text-xs font-medium ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                        {/* old While “Open for new enrollment” is on, students can also pick this plan for themselves */}
                        While “Open for new enrollment” is on, clients can also pick this plan for themselves
                        {priceNum > 0 ? ` at $${priceNum.toFixed(2)} ${formData.billing_frequency === 'yearly' ? 'a year' : 'a month'}` : ' for free'}.
                        {/* old Turn it off to keep it for Community Partners' students. */}
                        {" "}Turn it off to keep it for Community Partners' clients.
                    </p>
                )}
            </div>
            {error && (
                <div role="alert" className={`p-3 rounded-lg text-sm border ${isDarkMode ? 'bg-red-900/30 border-red-800 text-red-300' : 'bg-red-50 border-red-200 text-red-700'}`}>
                    {error}
                </div>
            )}
            <div className="flex gap-2 pt-2">
                <button
                    onClick={submit}
                    //old disabled={saving || !formData.tier_name.trim() || audienceIds.length === 0 || (!isFreeForever && !formData.payment_link.trim())
                    // (a Stripe price made when the tier is saved isn't typed: autoPrices)
                    disabled={saving || !formData.tier_name.trim() || audienceIds.length === 0 || (!isFreeForever && !formData.payment_link.trim() && !priceMadeOnSave)
                        //old || Boolean(priceProblem || durationProblem || trialProblem) || (groupOn && discountProblems.some(Boolean))}
                        || Boolean(priceProblem || durationProblem || trialProblem) || (groupOn && discountProblems.some(Boolean))
                        // a package price the admin typed that isn't written yet (Save would send something else)
                        || (formData.is_community_partner_tier && Boolean(p50Problem || p100Problem))}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50"
                >
                    <Save size={18} />
                    {saving ? 'Saving...' : 'Save'}
                </button>
                <button
                    onClick={onCancel}
                    className={`flex items-center gap-2 px-4 py-2 border rounded-lg ${isDarkMode ? 'border-slate-600 hover:bg-slate-700' : 'border-slate-300 hover:bg-slate-50'}`}
                >
                    <X size={18} />
                    Cancel
                </button>
            </div>
        </div>
    );
}

//function CodeEditForm({ code, tiers, onSave, onCancel, saving }) {
function CodeEditForm({ code, tiers, onSave, onCancel, saving, error }) {
    const { isDarkMode } = useTheme();
    const [formData, setFormData] = React.useState({
        code: code?.Code || '',
        discount_type: code?.DiscountType || 'free',
        discount_value: code?.DiscountValue != null ? Number(code.DiscountValue) : '',
        max_redemptions: code?.MaxRedemptions != null ? Number(code.MaxRedemptions) : '',
        expires_at: code?.ExpiresAt ? String(code.ExpiresAt).slice(0, 10) : '',
        is_active: code?.isActive !== undefined ? (code.isActive ? 1 : 0) : 1,
    });
    const [tierIds, setTierIds] = React.useState((code?.Tiers || []).map(t => t.TierID));

    const toggleTier = (id) => {
        setTierIds((prev) => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const submit = () => onSave({
        code: formData.code ? formData.code.trim().toUpperCase() : '',
        tier_ids: tierIds,
        discount_type: formData.discount_type,
        discount_value: formData.discount_type === 'free' ? null : (formData.discount_value === '' ? null : Number(formData.discount_value)),
        max_redemptions: formData.max_redemptions === '' ? null : parseInt(formData.max_redemptions),
        expires_at: formData.expires_at || null,
        is_active: formData.is_active,
    });

    const needsValue = formData.discount_type !== 'free';
    // Same rules as the server: more than 0, and a percent at most 100
    const discountNum = Number(formData.discount_value);
    const discountProblem = !needsValue || formData.discount_value === '' ? ''
        : !(discountNum > 0) ? 'The discount has to be more than 0.'
            : formData.discount_type === 'percent' && discountNum > 100 ? "A percent discount can't be more than 100%." : '';
    // Only active tiers are grantable; keep any tier already on the code even if
    // it's since gone inactive.
    const grantableTiers = (tiers || []).filter(t => t.IsActive || tierIds.includes(t.TierID));
    // How long a free code gives each picked plan: the plan's Days given free (Edit Tier)
    const freeLengths = grantableTiers.filter((t) => tierIds.includes(t.TierID))
        .map((t) => `${t.TierName} for ${t.DurationDays || 36500} days`).join(', ');

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                        Code <span className="font-normal text-xs opacity-70">(blank = auto-generate)</span>
                    </label>
                    <input
                        type="text"
                        value={formData.code}
                        onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                        disabled={!!code}
                        className={`w-full p-2 border rounded-lg font-mono ${isDarkMode ? 'bg-slate-900 border-slate-600 text-slate-200' : 'border-slate-300'} ${code ? 'opacity-60' : ''}`}
                        placeholder="e.g., COACHFREE"
                    />
                </div>
                <div>
                    <label className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Status</label>
                    <select
                        value={formData.is_active}
                        onChange={(e) => setFormData({ ...formData, is_active: parseInt(e.target.value) })}
                        className={`select-styled w-full text-sm font-medium rounded-xl px-3 py-2.5 border shadow-sm ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-200' : 'border-slate-300 bg-white text-slate-700'}`}
                    >
                        <option value={1}>Active</option>
                        <option value={0}>Inactive</option>
                    </select>
                </div>
                <div className="md:col-span-2">
                    <label className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Applies to tiers *</label>
                    <div className="flex flex-wrap gap-2">
                        {grantableTiers.map(t => {
                            const on = tierIds.includes(t.TierID);
                            return (
                                <button
                                    type="button"
                                    key={t.TierID}
                                    onClick={() => toggleTier(t.TierID)}
                                    className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors ${
                                        on
                                            ? 'bg-blue-600 border-blue-600 text-white'
                                            : isDarkMode ? 'border-slate-600 text-slate-300 hover:border-slate-500' : 'border-slate-300 text-slate-600 hover:border-slate-400'
                                    }`}
                                >
                                    {/* old {on ? '✓ ' : ''}{t.TierName}{t.Audiences && t.Audiences.length ? ` (${t.Audiences.join(', ')})` : ''} */}
                                    {on ? '✓ ' : ''}{t.TierName}{t.Audiences && t.Audiences.length ? ` (${t.Audiences.map((a) => AUDIENCE_LABEL[a] || a).join(', ')})` : ''}
                                </button>
                            );
                        })}
                        {grantableTiers.length === 0 && (
                            <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>No active tiers.</span>
                        )}
                    </div>
                    <p className={`mt-1 text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Pick one or more active tiers. A coach redeeming a free code gets a Coach tier from this set.</p>
                </div>
                <div>
                    <label className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Discount Type</label>
                    <select
                        value={formData.discount_type}
                        onChange={(e) => setFormData({ ...formData, discount_type: e.target.value })}
                        className={`select-styled w-full text-sm font-medium rounded-xl px-3 py-2.5 border shadow-sm ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-200' : 'border-slate-300 bg-white text-slate-700'}`}
                    >
                        <option value="free">Free (full grant)</option>
                        <option value="percent">Percent off</option>
                        <option value="amount">Amount off</option>
                    </select>
                </div>
                <div>
                    <label className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                        {formData.discount_type === 'percent' ? 'Percent (%)' : formData.discount_type === 'amount' ? 'Amount ($)' : 'Discount value'} {needsValue ? '*' : ''}
                    </label>
                    <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        max={formData.discount_type === 'percent' ? 100 : undefined}
                        value={needsValue ? formData.discount_value : ''}
                        disabled={!needsValue}
                        onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                        placeholder={needsValue ? '' : 'Not needed for a free code'}
                        className={`w-full p-2 border rounded-lg ${isDarkMode ? 'bg-slate-900 border-slate-600 text-slate-200' : 'border-slate-300'} ${!needsValue ? 'opacity-50 cursor-not-allowed bg-slate-100' : ''}`}
                    />
                    {discountProblem && <p className="mt-1 text-xs font-medium text-red-500">{discountProblem}</p>}
                </div>
                <div>
                    <label className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                        {/* Expires <span className="font-normal text-xs opacity-70">(blank = never)</span> */}
                        {/* old Expires <span className="font-normal text-xs opacity-70">(works through the end of that day, Central time · blank = never)</span> */}
                        Offer ends <span className="font-normal text-xs opacity-70">(last day to use the code, through the end of that day, Central time)</span>
                    </label>
                    <input
                        type="date"
                        value={formData.expires_at}
                        onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                        className={`w-full p-2 border rounded-lg ${isDarkMode ? 'bg-slate-900 border-slate-600 text-slate-200' : 'border-slate-300'}`}
                    />
                    {/* The date ends a percent or amount discount for everyone who uses the code while it has that date
                        (Stripe lowers only the payments before it); a free code gives each plan its Days given free */}
                    <p className={`mt-1 text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        {!needsValue
                            ? `A free code gives ${freeLengths || 'each tier for its Days given free (Edit Tier)'}. Blank: the code never ends.`
                            // the Stripe discount of someone who already paid with it was made then, to the date the code had
                            : code?.TimesRedeemed > 0
                                ? 'After this day, payments are full price again for people who use the code from now on. Blank: the code and their discount never end. People who already used it keep the date the code had when they did (no date then: their discount never ends).'
                                : 'After this day, payments are full price again, also for people who already used the code. Blank: the code and its discount never end.'}
                    </p>
                </div>
                <div>
                    <label className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                        Max Redemptions <span className="font-normal text-xs opacity-70">(blank = unlimited)</span>
                    </label>
                    <input
                        type="number"
                        value={formData.max_redemptions}
                        onChange={(e) => setFormData({ ...formData, max_redemptions: e.target.value })}
                        className={`w-full p-2 border rounded-lg ${isDarkMode ? 'bg-slate-900 border-slate-600 text-slate-200' : 'border-slate-300'}`}
                        placeholder="Unlimited"
                    />
                </div>
            </div>
            {error && (
                <div role="alert" className={`p-3 rounded-lg text-sm border ${isDarkMode ? 'bg-red-900/30 border-red-800 text-red-300' : 'bg-red-50 border-red-200 text-red-700'}`}>
                    {error}
                </div>
            )}
            <div className="flex gap-2 pt-2">
                <button
                    onClick={submit}
                    //disabled={saving || tierIds.length === 0 || (needsValue && formData.discount_value === '')}
                    disabled={saving || tierIds.length === 0 || (needsValue && formData.discount_value === '') || Boolean(discountProblem)}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50"
                >
                    <Save size={18} />
                    {saving ? 'Saving...' : 'Save'}
                </button>
                <button onClick={onCancel} className={`flex items-center gap-2 px-4 py-2 border rounded-lg ${isDarkMode ? 'border-slate-600 hover:bg-slate-700' : 'border-slate-300 hover:bg-slate-50'}`}>
                    <X size={18} />
                    Cancel
                </button>
            </div>
        </div>
    );
}
